import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { GripVertical, ImagePlus, Mic, MicOff, Plus, Trash2, Download, Loader2, ArrowLeft, Save, Undo2, Eye, Wand2 } from "lucide-react";
import RichContentEditor, {
  RichContentRenderer,
  type RichContentEditorHandle,
} from "../../components/question-bank/RichContentEditor";
import Modal from "../../components/ui/Modal";
import { useModal } from "../../hooks/useModal";
import { ApiService } from "../../services/api";
import type {
  ChoiceKey,
  QuestionBankQuestion as QuestionItem,
  QuestionBankState,
  QuestionImage,
  RichContentDocument,
  SketchElement,
  SketchShapeType,
  QuestionTemplateType,
  QuestionChoiceLayout,
  SketchTableElement,
  SketchTableCellTextMap,
} from "../../types/question-bank";
import {
  createRichContentDocument,
  extractPlainTextFromRichContent,
  getChoiceContent,
  normalizeRichContentDocument,
  setChoiceContent,
  setQuestionContent,
} from "../../utils/questionRichContent";

const CHOICE_KEYS: ChoiceKey[] = ["A", "B", "C", "D", "E"];
const MIN_CHOICE_COUNT = 2;
const MAX_CHOICE_COUNT = CHOICE_KEYS.length;
const MAX_IMAGE_TRIM_BOTTOM_RATIO = 0.7;
const MIN_QUESTION_IMAGE_SCALE = 0.5;
const MAX_QUESTION_IMAGE_SCALE = 1.8;
const QUESTION_IMAGE_PREVIEW_BASE_HEIGHT = 240;
const QUESTION_TEMPLATE_OPTIONS: Array<{
  type: QuestionTemplateType;
  label: string;
  description: string;
  choiceLayout: QuestionChoiceLayout;
}> = [
  {
    type: "classic_vertical",
    label: "Klasik",
    description: "Soru metni ve şıklar alt alta klasik soru bankası düzeni.",
    choiceLayout: "vertical",
  },
  {
    type: "image_with_caption",
    label: "Görsel Açıklamalı",
    description: "Üstte kısa açıklama, altında görsel ve soru metni olan düzen.",
    choiceLayout: "vertical",
  },
  {
    type: "horizontal_choices",
    label: "Yatay Şıklı",
    description: "Şıkların kompakt ve yatay hizalandığı soru bankası düzeni.",
    choiceLayout: "horizontal",
  },
];
type MathTargetField = "question" | ChoiceKey;
type MathPickerTargetState = {
  questionId: string;
  field: MathTargetField;
} | null;
type MathPickerPosition = {
  top: number;
  left: number;
  maxHeight: number;
} | null;
type MathPickerPlacement = "above" | "below";
type MathSymbolAction = {
  id: string;
  label: string;
  hint: string;
  mode: "insert" | "wrap";
  insert?: string;
  prefix?: string;
  suffix?: string;
  cursorOffset?: number;
};
type MathSymbolGroup = {
  title: string;
  items: MathSymbolAction[];
};
type SketchTool = "pen" | "text";
type SketchHistoryEntry = {
  snapshot: string;
  elements: SketchElement[];
};
type SketchDraftElement = {
  type: SketchPlacementTool;
  x: number;
  y: number;
  width: number;
  height: number;
};
type ActiveTableCellEdit = {
  elementId: string;
  row: number;
  column: number;
} | null;
type SketchPlacementTool = "text" | SketchShapeType | "table";

const SKETCH_CANVAS_WIDTH = 920;
const SKETCH_CANVAS_HEIGHT = 260;
const DEFAULT_TEXTBOX_WIDTH = 180;
const DEFAULT_TEXTBOX_HEIGHT = 64;
const DEFAULT_SHAPE_SIZE = 88;
const DEFAULT_TABLE_CELL_WIDTH = 72;
const DEFAULT_TABLE_CELL_HEIGHT = 40;
const MIN_TABLE_SIZE = 2;
const MAX_TABLE_SIZE = 12;
const TABLE_CELL_TEXT_PADDING_X = 8;
const TABLE_CELL_TEXT_PADDING_Y = 6;
const MIN_TABLE_CELL_FONT_SIZE = 10;
const MAX_TABLE_CELL_FONT_SIZE = 22;
const SHAPE_OPTIONS: Array<{ type: SketchShapeType; label: string }> = [
  { type: "rectangle", label: "Dikdörtgen" },
  { type: "square", label: "Kare" },
  { type: "triangle", label: "Üçgen" },
  { type: "circle", label: "Yuvarlak" },
];
const SKETCH_LIBRARY_ITEMS: Array<{ type: SketchPlacementTool; label: string }> = [
  { type: "text", label: "Metin Kutusu" },
  { type: "table", label: "Tablo" },
  ...SHAPE_OPTIONS,
];
const MIN_ELEMENT_SIZE = 18;

const MATH_SYMBOL_GROUPS: MathSymbolGroup[] = [
  {
    title: "Temel",
    items: [
      { id: "plus-minus", label: "±", hint: "Artı eksi", mode: "insert", insert: "±" },
      { id: "times", label: "×", hint: "Çarpma", mode: "insert", insert: "×" },
      { id: "divide", label: "÷", hint: "Bölme", mode: "insert", insert: "÷" },
      { id: "not-equal", label: "≠", hint: "Eşit değildir", mode: "insert", insert: "≠" },
      { id: "less-equal", label: "≤", hint: "Küçük eşittir", mode: "insert", insert: "≤" },
      { id: "greater-equal", label: "≥", hint: "Büyük eşittir", mode: "insert", insert: "≥" },
      { id: "approx", label: "≈", hint: "Yaklaşık eşittir", mode: "insert", insert: "≈" },
      { id: "infinity", label: "∞", hint: "Sonsuzluk", mode: "insert", insert: "∞" },
      { id: "pi", label: "π", hint: "Pi", mode: "insert", insert: "π" },
      { id: "degree", label: "°", hint: "Derece", mode: "insert", insert: "°" },
    ],
  },
  {
    title: "Kök ve Üs",
    items: [
      { id: "sqrt", label: "√( )", hint: "Seçili metni karekök içine alır", mode: "wrap", prefix: "√(", suffix: ")" },
      { id: "cuberoot", label: "∛( )", hint: "Seçili metni küpkök içine alır", mode: "wrap", prefix: "∛(", suffix: ")" },
      { id: "power2", label: "( )²", hint: "Seçili metnin karesini alır", mode: "wrap", prefix: "(", suffix: ")²" },
      { id: "power3", label: "( )³", hint: "Seçili metnin küpünü alır", mode: "wrap", prefix: "(", suffix: ")³" },
      { id: "power-n", label: "ⁿ", hint: "Üs n", mode: "insert", insert: "ⁿ" },
      { id: "sub-1", label: "₁", hint: "Alt indis 1", mode: "insert", insert: "₁" },
      { id: "sub-2", label: "₂", hint: "Alt indis 2", mode: "insert", insert: "₂" },
      { id: "fraction", label: "( )/( )", hint: "Pay ve payda şablonu ekler", mode: "insert", insert: "()/()", cursorOffset: 1 },
    ],
  },
  {
    title: "Geometri ve Küme",
    items: [
      { id: "angle", label: "∠", hint: "Açı", mode: "insert", insert: "∠" },
      { id: "triangle", label: "△", hint: "Üçgen", mode: "insert", insert: "△" },
      { id: "perpendicular", label: "⊥", hint: "Diklik", mode: "insert", insert: "⊥" },
      { id: "parallel", label: "∥", hint: "Paralellik", mode: "insert", insert: "∥" },
      { id: "belongs", label: "∈", hint: "Elemanıdır", mode: "insert", insert: "∈" },
      { id: "not-belongs", label: "∉", hint: "Elemanı değildir", mode: "insert", insert: "∉" },
      { id: "union", label: "∪", hint: "Birleşim", mode: "insert", insert: "∪" },
      { id: "intersection", label: "∩", hint: "Kesişim", mode: "insert", insert: "∩" },
    ],
  },
  {
    title: "Türev ve İntegral",
    items: [
      { id: "integral", label: "∫", hint: "İntegral", mode: "insert", insert: "∫" },
      { id: "double-integral", label: "∬", hint: "Çift integral", mode: "insert", insert: "∬" },
      { id: "triple-integral", label: "∭", hint: "Üçlü integral", mode: "insert", insert: "∭" },
      { id: "partial", label: "∂", hint: "Kısmi türev sembolü", mode: "insert", insert: "∂" },
      { id: "nabla", label: "∇", hint: "Nabla", mode: "insert", insert: "∇" },
      { id: "derivative", label: "d/dx( )", hint: "Türev şablonu", mode: "insert", insert: "d/dx()", cursorOffset: 1 },
      { id: "partial-derivative", label: "∂/∂x( )", hint: "Kısmi türev şablonu", mode: "insert", insert: "∂/∂x()", cursorOffset: 1 },
      { id: "limit", label: "lim", hint: "Limit", mode: "insert", insert: "lim" },
      { id: "sum", label: "∑", hint: "Toplam", mode: "insert", insert: "∑" },
      { id: "product", label: "∏", hint: "Çarpım", mode: "insert", insert: "∏" },
    ],
  },
  {
    title: "Hazır Kalıp",
    items: [
      { id: "absolute", label: "| |", hint: "Mutlak değer", mode: "wrap", prefix: "|", suffix: "|" },
      { id: "parentheses", label: "( )", hint: "Parantez", mode: "wrap", prefix: "(", suffix: ")" },
      { id: "brackets", label: "[ ]", hint: "Köşeli parantez", mode: "wrap", prefix: "[", suffix: "]" },
      { id: "set", label: "{ }", hint: "Küme parantezi", mode: "wrap", prefix: "{", suffix: "}" },
    ],
  },
];

function MathSymbolPicker({
  onSelect,
  maxHeight,
}: {
  onSelect: (action: MathSymbolAction) => void;
  maxHeight: number;
}) {
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState(MATH_SYMBOL_GROUPS[0]?.title || "");

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");

    if (!query) {
      return MATH_SYMBOL_GROUPS.filter((group) => group.title === activeGroup);
    }

    return MATH_SYMBOL_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const label = item.label.toLocaleLowerCase("tr-TR");
        const hint = item.hint.toLocaleLowerCase("tr-TR");
        return label.includes(query) || hint.includes(query);
      }),
    })).filter((group) => group.items.length > 0);
  }, [activeGroup, search]);

  return (
    <div
      className="w-[min(92vw,520px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.14)]"
      style={{ maxHeight }}>
      <div className="border-b border-slate-200 bg-slate-50 px-3 py-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {MATH_SYMBOL_GROUPS.map((group) => (
            <button
              key={group.title}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setActiveGroup(group.title)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                activeGroup === group.title
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
              }`}
            >
              {group.title}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sembol ara"
          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-300 focus:outline-none"
        />
      </div>

      <div
        className="overflow-y-auto px-4 py-4 space-y-5"
        style={{ maxHeight: Math.max(140, maxHeight - 92) }}>
        {filteredGroups.map((group) => (
          <div key={group.title}>
            <div className="mb-3 text-sm font-semibold text-slate-700">{group.title}</div>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  title={item.hint}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSelect(item)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-center text-sm font-medium text-slate-800 transition hover:border-blue-200 hover:bg-slate-50"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        ))}

        {filteredGroups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
            Aramaya uygun sembol bulunamadı.
          </div>
        ) : null}
      </div>
    </div>
  );
}

const INDESIGN_JSX_CONTENT = `// JSON dosyasını seçtirme
var jsonFile = File.openDialog("Lütfen manifest.json dosyasını seçin", "*.json");
if (jsonFile != null) {
jsonFile.open("r");
jsonFile.encoding = "UTF-8";
var content = jsonFile.read();
jsonFile.close();

try {
    var data = eval("(" + content + ")");
    var sorular = data.sorular;
    
    var doc = app.documents.add();
    doc.viewPreferences.horizontalMeasurementUnits = MeasurementUnits.MILLIMETERS;
    doc.viewPreferences.verticalMeasurementUnits = MeasurementUnits.MILLIMETERS;
    doc.documentPreferences.facingPages = false;
    
    // --- YENİ: STANDART SORU BANKASI SAYFA BOYUTU (19x27 cm) ---
    doc.documentPreferences.pageWidth = 190;
    doc.documentPreferences.pageHeight = 270;
    
    var basePath = jsonFile.parent.fsName; 
    
    var page = doc.pages[0];
    var startX = 15;        // Sol kenar boşluğu
    var startY = 15;        // Üst kenar boşluğu
    var colWidth = 75;      // Sütun genişliği (190mm kağıda göre uyarlandı)
    var gutter = 10;        // İki sütun arası boşluk
    var maxY = 250;         // Sayfa alt sınırı (270mm kağıt boyuna göre)
    
    function drawCenterLine(targetPage) {
        var lineX = startX + colWidth + (gutter / 2);
        var line = targetPage.graphicLines.add();
        line.geometricBounds = [startY, lineX, maxY, lineX];
        line.strokeWeight = 0.5; 
        try { line.strokeTint = 40; } catch(e){} 
    }
    
    function getChoicesText(soru) {
        var secenekSayisi = soru.secenek_sayisi || 5;
        var keys = ["A", "B", "C", "D", "E"].slice(0, secenekSayisi);
        var lines = [];
        for (var i = 0; i < keys.length; i++) {
            var key = keys[i];
            lines.push(key + ") " + (soru.siklar[key] || ""));
        }
        return lines.join("\\n");
    }

    function measureQuestionHeight(targetPage, soru, currX) {
        var total = 0;
        var txtFrame = targetPage.textFrames.add();
        txtFrame.geometricBounds = [0, currX, 10, currX + colWidth];
        txtFrame.textFramePreferences.autoSizingType = AutoSizingTypeEnum.HEIGHT_ONLY;
        txtFrame.textFramePreferences.autoSizingReferencePoint = AutoSizingReferenceEnum.TOP_LEFT_POINT;
        txtFrame.contents = soru.sira + ". " + soru.soru_metni;

        var story = txtFrame.parentStory;
        story.pointSize = 10.5;
        story.leading = 15;
        try { story.words[0].fontStyle = "Bold"; } catch(e){}
        try { story.recompose(); } catch(e){}
        total += (txtFrame.geometricBounds[2] - txtFrame.geometricBounds[0]) + 4;

        var hasImage = soru.gorsel_yolu && soru.gorsel_yolu !== "null" && soru.gorsel_yolu !== "";
        if (hasImage) total += 50 + 4;

        var siklarFrame = targetPage.textFrames.add();
        siklarFrame.geometricBounds = [0, currX, 10, currX + colWidth];
        siklarFrame.textFramePreferences.autoSizingType = AutoSizingTypeEnum.HEIGHT_ONLY;
        siklarFrame.textFramePreferences.autoSizingReferencePoint = AutoSizingReferenceEnum.TOP_LEFT_POINT;
        siklarFrame.contents = getChoicesText(soru);

        var siklarStory = siklarFrame.parentStory;
        siklarStory.pointSize = 10.5;
        siklarStory.leading = 17;
        try {
            for(var p = 0; p < siklarStory.paragraphs.length; p++) {
                siklarStory.paragraphs[p].characters.itemByRange(0, 1).fontStyle = "Bold";
            }
        } catch(e){}
        try { siklarStory.recompose(); } catch(e){}
        total += (siklarFrame.geometricBounds[2] - siklarFrame.geometricBounds[0]);

        try { txtFrame.remove(); } catch(e){}
        try { siklarFrame.remove(); } catch(e){}
        return total;
    }

    function placeQuestion(targetPage, soru, currX, y) {
        var tempY = y;
        var txtFrame = targetPage.textFrames.add();
        txtFrame.geometricBounds = [tempY, currX, tempY + 10, currX + colWidth];
        txtFrame.textFramePreferences.autoSizingType = AutoSizingTypeEnum.HEIGHT_ONLY;
        txtFrame.textFramePreferences.autoSizingReferencePoint = AutoSizingReferenceEnum.TOP_LEFT_POINT;
        txtFrame.contents = soru.sira + ". " + soru.soru_metni;

        var story = txtFrame.parentStory;
        story.pointSize = 10.5;
        story.leading = 15;
        try { story.words[0].fontStyle = "Bold"; } catch(e){}
        try { story.recompose(); } catch(e){}
        tempY = txtFrame.geometricBounds[2] + 4;

        if (soru.gorsel_yolu && soru.gorsel_yolu !== "null" && soru.gorsel_yolu !== "") {
            var imagePath = new File(basePath + "/" + soru.gorsel_yolu);
            if (imagePath.exists) {
                var imgFrame = targetPage.rectangles.add();
                var imgX = currX + 2;
                var imgMaxWidth = colWidth - 4;
                imgFrame.geometricBounds = [tempY, imgX, tempY + 50, imgX + imgMaxWidth];
                imgFrame.place(imagePath);
                imgFrame.fit(FitOptions.PROPORTIONALLY);
                imgFrame.fit(FitOptions.FRAME_TO_CONTENT);
            }
            tempY += 50 + 4;
        }

        var siklarFrame = targetPage.textFrames.add();
        siklarFrame.geometricBounds = [tempY, currX, tempY + 10, currX + colWidth];
        siklarFrame.textFramePreferences.autoSizingType = AutoSizingTypeEnum.HEIGHT_ONLY;
        siklarFrame.textFramePreferences.autoSizingReferencePoint = AutoSizingReferenceEnum.TOP_LEFT_POINT;
        siklarFrame.contents = getChoicesText(soru);

        var siklarStory = siklarFrame.parentStory;
        siklarStory.pointSize = 10.5;
        siklarStory.leading = 17;
        try {
            for(var p = 0; p < siklarStory.paragraphs.length; p++) {
                siklarStory.paragraphs[p].characters.itemByRange(0, 1).fontStyle = "Bold";
            }
        } catch(e){}
        try { siklarStory.recompose(); } catch(e){}
        return siklarFrame.geometricBounds[2];
    }

    function getRowHeights(targetPage, leftQuestions, rightQuestions) {
        var rows = Math.max(leftQuestions.length, rightQuestions.length);
        var rowHeights = [];
        for (var r = 0; r < rows; r++) {
            var leftH = 0;
            var rightH = 0;
            if (r < leftQuestions.length) leftH = measureQuestionHeight(targetPage, leftQuestions[r], startX);
            if (r < rightQuestions.length) rightH = measureQuestionHeight(targetPage, rightQuestions[r], startX + colWidth + gutter);
            rowHeights.push(Math.max(leftH, rightH));
        }
        return rowHeights;
    }

    function canFitPageCount(targetPage, startIndex, count) {
        var pageQuestions = sorular.slice(startIndex, startIndex + count);
        var leftCount = count <= 3 ? count : 3;
        var leftQuestions = pageQuestions.slice(0, leftCount);
        var rightQuestions = pageQuestions.slice(leftCount);

        var rowHeights = getRowHeights(targetPage, leftQuestions, rightQuestions);
        var minGap = 6;
        var totalNeeded = 0;
        for (var i = 0; i < rowHeights.length; i++) totalNeeded += rowHeights[i];
        if (rowHeights.length > 1) totalNeeded += (rowHeights.length - 1) * minGap;
        return totalNeeded <= (maxY - startY);
    }

    function placeRowsAligned(targetPage, leftQuestions, rightQuestions) {
        var rows = Math.max(leftQuestions.length, rightQuestions.length);
        if (rows === 0) return;

        var rowHeights = getRowHeights(targetPage, leftQuestions, rightQuestions);
        var totalHeights = 0;
        for (var i = 0; i < rowHeights.length; i++) totalHeights += rowHeights[i];

        var minGap = 6;
        var gap = 0;
        if (rows > 1) {
            gap = Math.max(minGap, ((maxY - startY) - totalHeights) / (rows - 1));
        }

        var y = startY;
        for (var r = 0; r < rows; r++) {
            if (r < leftQuestions.length) {
                placeQuestion(targetPage, leftQuestions[r], startX, y);
            }
            if (r < rightQuestions.length) {
                placeQuestion(targetPage, rightQuestions[r], startX + colWidth + gutter, y);
            }
            y += rowHeights[r] + (r < rows - 1 ? gap : 0);
        }
    }

    var index = 0;
    while (index < sorular.length) {
        var remaining = sorular.length - index;
        var countForPage = Math.min(6, remaining);

        while (countForPage > 1 && !canFitPageCount(page, index, countForPage)) {
            countForPage--;
        }

        var pageQuestions = sorular.slice(index, index + countForPage);
        var leftCount = countForPage <= 3 ? countForPage : 3;
        var leftQuestions = pageQuestions.slice(0, leftCount);
        var rightQuestions = pageQuestions.slice(leftCount);

        drawCenterLine(page);
        placeRowsAligned(page, leftQuestions, rightQuestions);

        index += countForPage;
        if (index < sorular.length) page = doc.pages.add(LocationOptions.AT_END);
    }
__ANSWER_KEY_SECTION__
    alert("İşlem Tamam! Tüm sorular 19x27 cm Dizgici formatında dizildi.");
    
} catch (e) {
    alert("Bir hata oluştu. Hata: " + e);
}
}`;

const ANSWER_KEY_JSX_SECTION = `
    function resolveBlackSwatch() {
        var names = ["Black", "[Black]", "Siyah", "[Siyah]"];
        for (var i = 0; i < names.length; i++) {
            var sw = doc.swatches.itemByName(names[i]);
            if (sw.isValid) return sw;
        }
        var c = doc.colors.add({
            name: "YA_Pure_Black",
            model: ColorModel.PROCESS,
            space: ColorSpace.CMYK,
            colorValue: [0, 0, 0, 100]
        });
        return c;
    }

    function resolvePaperSwatch() {
        var names = ["Paper", "[Paper]", "Kağıt", "[Kağıt]"];
        for (var i = 0; i < names.length; i++) {
            var sw = doc.swatches.itemByName(names[i]);
            if (sw.isValid) return sw;
        }
        return doc.swatches.firstItem();
    }

    var keyTitleColor = resolveBlackSwatch();
    var keyLineColor = resolveBlackSwatch();
    var keyPaper = resolvePaperSwatch();

    var keyPageRows = 24;
    var keyPageCols = 5;
    var keyPerPage = keyPageRows * keyPageCols;
    var keyIndex = 0;
    var keyPageNo = 0;

    while (keyIndex < sorular.length) {
        var keyPage = doc.pages.add(LocationOptions.AT_END);
        keyPageNo++;

        var keyTitle = keyPage.textFrames.add();
        keyTitle.geometricBounds = [14, 15, 24, 175];
        keyTitle.contents = "CEVAP ANAHTARI";
        var keyTitleStory = keyTitle.parentStory;
        keyTitleStory.pointSize = 15;
        keyTitleStory.leading = 17;
        keyTitleStory.justification = Justification.CENTER_ALIGN;
        try { keyTitleStory.fontStyle = "Bold"; } catch (e) {}
        try { keyTitle.texts[0].fillColor = keyTitleColor; } catch (e) {}
        try { keyTitle.strokeColor = keyPaper; keyTitle.strokeWeight = 0; keyTitle.fillColor = keyPaper; } catch (e) {}

        var keySubtitle = keyPage.textFrames.add();
        keySubtitle.geometricBounds = [24, 15, 31, 175];
        keySubtitle.contents = (data.test_adi ? String(data.test_adi) : "Test") + (data.kategori ? (" - " + String(data.kategori)) : "");
        var keySubtitleStory = keySubtitle.parentStory;
        keySubtitleStory.pointSize = 10;
        keySubtitleStory.leading = 12;
        keySubtitleStory.justification = Justification.CENTER_ALIGN;
        try { keySubtitle.texts[0].fillColor = keyTitleColor; } catch (e) {}
        try { keySubtitle.strokeColor = keyPaper; keySubtitle.strokeWeight = 0; keySubtitle.fillColor = keyPaper; } catch (e) {}

        var tableX = 15;
        var tableY = 36;
        var tableW = 160;
        var cellH = 8;
        var colGap = 2.5;
        var cellW = (tableW - ((keyPageCols - 1) * colGap)) / keyPageCols;

        for (var i = 0; i < keyPerPage && (keyIndex + i) < sorular.length; i++) {
            var q = sorular[keyIndex + i];
            var row = Math.floor(i / keyPageCols);
            var col = i % keyPageCols;
            var x = tableX + (col * (cellW + colGap));
            var y = tableY + (row * cellH);

            var cell = keyPage.rectangles.add();
            cell.geometricBounds = [y, x, y + cellH, x + cellW];
            cell.strokeWeight = 0.4;
            try { cell.strokeColor = keyLineColor; } catch (e) {}
            try { cell.fillColor = keyPaper; } catch (e) {}

            var cellText = keyPage.textFrames.add();
            cellText.geometricBounds = [y + 1, x + 1.5, y + cellH - 0.6, x + cellW - 1.5];
            cellText.contents = q.sira + ". " + (q.dogru_cevap ? q.dogru_cevap : "-");
            var cellStory = cellText.parentStory;
            cellStory.pointSize = 9.5;
            cellStory.leading = 11;
            try { cellStory.fontStyle = "Bold"; } catch (e) {}
            try { cellText.texts[0].fillColor = keyTitleColor; } catch (e) {}
            try { cellText.strokeWeight = 0; } catch (e) {}
            cellText.textFramePreferences.verticalJustification = VerticalJustification.CENTER_ALIGN;
            cellText.texts[0].justification = Justification.CENTER_ALIGN;
        }

        keyIndex += keyPerPage;
    }
`;

const buildInDesignJsxContent = (includeAnswerKey: boolean) =>
  INDESIGN_JSX_CONTENT.replace("__ANSWER_KEY_SECTION__", includeAnswerKey ? ANSWER_KEY_JSX_SECTION : "");

const createEmptyQuestion = (sira: number): QuestionItem => ({
  id: crypto.randomUUID(),
  sira,
  soru_metni: "",
  questionContent: createRichContentDocument(""),
  templateType: "classic_vertical",
  intro_text: "",
  showIntroText: false,
  imagePlacement: "top",
  choiceLayout: "vertical",
  siklar: { A: "", B: "", C: "", D: "", E: "" },
  choiceContents: {
    A: createRichContentDocument(""),
    B: createRichContentDocument(""),
    C: createRichContentDocument(""),
    D: createRichContentDocument(""),
    E: createRichContentDocument(""),
  },
  secenek_sayisi: MAX_CHOICE_COUNT,
  dogru_cevap: null,
  image: null,
  referenceImage: null,
  sketchBase64: null,
  sketchElements: null,
});

const clampChoiceCount = (value: number) => Math.min(MAX_CHOICE_COUNT, Math.max(MIN_CHOICE_COUNT, value));
const clampImageTrimBottomRatio = (value: number) =>
  Math.min(MAX_IMAGE_TRIM_BOTTOM_RATIO, Math.max(0, Number.isFinite(Number(value)) ? Number(value) : 0));
const clampQuestionImageScale = (value: number) =>
  Math.min(MAX_QUESTION_IMAGE_SCALE, Math.max(MIN_QUESTION_IMAGE_SCALE, Number.isFinite(Number(value)) ? Number(value) : 1));

const getVisibleChoiceKeys = (choiceCount: number) => CHOICE_KEYS.slice(0, clampChoiceCount(choiceCount));

const normalizeQuestionImage = (image: QuestionItem["image"]): QuestionItem["image"] => {
  if (!image) {
    return null;
  }

  return {
    ...image,
    trimBottomRatio: clampImageTrimBottomRatio(image.trimBottomRatio || 0),
    scale: clampQuestionImageScale(image.scale || 1),
  };
};

const normalizeQuestionForEditor = (question: QuestionItem): QuestionItem => {
  const secenek_sayisi = clampChoiceCount(Number(question?.secenek_sayisi) || MAX_CHOICE_COUNT);
  const visibleChoiceKeys = getVisibleChoiceKeys(secenek_sayisi);
  const templateType: QuestionTemplateType =
    question?.templateType === "image_with_caption" || question?.templateType === "horizontal_choices"
      ? question.templateType
      : "classic_vertical";
  const choiceLayout: QuestionChoiceLayout =
    question?.choiceLayout === "staggered"
      ? "staggered"
      : question?.choiceLayout === "horizontal" || templateType === "horizontal_choices"
        ? "horizontal"
        : "vertical";
  const questionContent = normalizeRichContentDocument(question?.questionContent || null, question?.soru_metni || "");
  const choiceContents = CHOICE_KEYS.reduce((acc, key) => {
    acc[key] = normalizeRichContentDocument(question?.choiceContents?.[key] || null, question?.siklar?.[key] || "");
    return acc;
  }, {} as Partial<Record<ChoiceKey, RichContentDocument | null>>);

  return {
    ...question,
    soru_metni: extractPlainTextFromRichContent(questionContent),
    questionContent,
    templateType,
    intro_text: typeof question?.intro_text === "string" ? question.intro_text : "",
    showIntroText: typeof question?.showIntroText === "boolean" ? question.showIntroText : Boolean((question?.intro_text || "").trim()),
    imagePlacement: "top",
    choiceLayout,
    siklar: CHOICE_KEYS.reduce((acc, key) => {
      const nextValue = extractPlainTextFromRichContent(choiceContents[key]);
      acc[key] = visibleChoiceKeys.includes(key) ? nextValue : "";
      return acc;
    }, {} as Record<ChoiceKey, string>),
    choiceContents,
    secenek_sayisi,
    dogru_cevap: question.dogru_cevap && visibleChoiceKeys.includes(question.dogru_cevap) ? question.dogru_cevap : null,
    image: normalizeQuestionImage(question?.image || null),
    sketchElements: Array.isArray(question?.sketchElements)
      ? question.sketchElements.map((element) => normalizeSketchElement(element))
      : null,
  };
};

const applySharedQuestionLayout = (
  question: QuestionItem,
  settings: {
    choiceLayout: QuestionChoiceLayout;
    showIntroText: boolean;
  }
): QuestionItem => {
  const isVisualQuestion = Boolean(question.image) || question.templateType === "image_with_caption";
  const templateType: QuestionTemplateType = isVisualQuestion
    ? "image_with_caption"
    : settings.choiceLayout === "horizontal"
      ? "horizontal_choices"
      : "classic_vertical";

  return normalizeQuestionForEditor({
    ...question,
    templateType,
    choiceLayout: settings.choiceLayout,
    showIntroText: settings.showIntroText,
    intro_text: question.intro_text || "",
  });
};

const PREVIEW_RICH_TEXT_CLASS_NAME = "text-sm leading-6 text-gray-800";

const buildQuestionBankUpdatePayload = (nextState: QuestionBankState) => ({
  testAdi: nextState.test_adi,
  kategori: nextState.kategori || null,
  questionCount: nextState.sorular.length,
  content: {
    ...nextState,
    soru_sayisi: nextState.sorular.length,
    sorular: nextState.sorular.map((question, index) => normalizeQuestionForEditor({ ...question, sira: index + 1 })),
  },
});

const getQuestionTemplateOption = (templateType: QuestionTemplateType) =>
  QUESTION_TEMPLATE_OPTIONS.find((option) => option.type === templateType) || QUESTION_TEMPLATE_OPTIONS[0];

function QuestionImagePreview({
  image,
  alt,
  imageClassName,
}: {
  image: NonNullable<QuestionItem["image"]>;
  alt: string;
  imageClassName: string;
}) {
  const trimBottomRatio = clampImageTrimBottomRatio(image.trimBottomRatio || 0);
  const imageScale = clampQuestionImageScale(image.scale || 1);

  return (
    <div className="relative overflow-hidden bg-white">
      <img
        src={`data:${image.mimeType};base64,${image.base64}`}
        alt={alt}
        className={imageClassName}
        style={{ height: `${QUESTION_IMAGE_PREVIEW_BASE_HEIGHT * imageScale}px` }}
      />
      {trimBottomRatio > 0 ? (
        <>
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 border-t border-dashed border-rose-300 bg-white/95"
            style={{ height: `${trimBottomRatio * 100}%` }}
          />
          <div className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-rose-600 px-2 py-1 text-[10px] font-semibold text-white shadow-sm">
            Alttan kırpılır
          </div>
        </>
      ) : null}
    </div>
  );
}

const renderQuestionPreviewChoices = (question: QuestionItem, choiceKeys: ChoiceKey[]) => {
  if (question.choiceLayout === "staggered") {
    const topRow =
      choiceKeys.length >= 5
        ? choiceKeys.slice(0, 3)
        : choiceKeys.length === 4
          ? choiceKeys.slice(0, 2)
          : choiceKeys.length === 3
            ? choiceKeys.slice(0, 2)
            : choiceKeys;
    const bottomRow =
      choiceKeys.length >= 5
        ? choiceKeys.slice(3, 5)
        : choiceKeys.length === 4
          ? choiceKeys.slice(2, 4)
          : choiceKeys.length === 3
            ? choiceKeys.slice(2, 3)
            : [];

    const renderChoice = (choice: ChoiceKey, widthClass: string) => (
      <div
        key={`preview_${choice}`}
        className={`${widthClass} rounded-lg border px-3 py-2 text-sm ${
          question.dogru_cevap === choice ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-white"
        }`}>
        <div className="flex items-start gap-2">
          <span className="font-semibold">{choice})</span>
          <div className="min-w-0 flex-1">
            <RichContentRenderer
              value={getChoiceContent(question, choice)}
              textClassName={PREVIEW_RICH_TEXT_CLASS_NAME}
              imageClassName="max-h-36 w-auto rounded-lg border border-gray-200 bg-white object-contain"
            />
          </div>
        </div>
      </div>
    );

    return (
      <div className="space-y-3">
        <div className="grid gap-2 grid-cols-6">
          {topRow.map((choice) => renderChoice(choice, topRow.length >= 3 ? "col-span-2" : "col-span-3"))}
        </div>
        {bottomRow.length ? (
          <div className="grid gap-2 grid-cols-6">
            <div className="col-span-1" />
            {bottomRow.map((choice) => renderChoice(choice, bottomRow.length === 1 ? "col-span-4" : "col-span-2"))}
            {bottomRow.length === 1 ? <div className="col-span-1" /> : <div className="col-span-1" />}
          </div>
        ) : null}
      </div>
    );
  }

  if (question.choiceLayout === "horizontal") {
    const visibleChoices = choiceKeys.slice(0, question.secenek_sayisi);

    return (
      <div
        className="grid items-start gap-2"
        style={{ gridTemplateColumns: `repeat(${Math.max(visibleChoices.length, 1)}, minmax(0, 1fr))` }}>
        {visibleChoices.map((choice) => (
          <div
            key={`preview_${choice}`}
            className={`min-w-0 rounded-lg border px-3 py-2 text-sm ${
              question.dogru_cevap === choice ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-white"
            }`}>
            <div className="flex items-start gap-2">
              <span className="shrink-0 font-semibold">{choice})</span>
              <div className="min-w-0 flex-1">
                <RichContentRenderer
                  value={getChoiceContent(question, choice)}
                  textClassName={PREVIEW_RICH_TEXT_CLASS_NAME}
                  imageClassName="max-h-32 w-auto rounded-lg border border-gray-200 bg-white object-contain"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {choiceKeys.map((choice) => (
        <div
          key={`preview_${choice}`}
          className={`rounded-lg border px-3 py-2 text-sm ${
            question.dogru_cevap === choice ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-white"
          }`}>
          <div className="flex items-start gap-2">
            <span className="font-semibold">{choice})</span>
            <div className="min-w-0 flex-1">
              <RichContentRenderer
                value={getChoiceContent(question, choice)}
                textClassName={PREVIEW_RICH_TEXT_CLASS_NAME}
                imageClassName="max-h-36 w-auto rounded-lg border border-gray-200 bg-white object-contain"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const hasRenderableRichContent = (document: RichContentDocument | null | undefined) =>
  normalizeRichContentDocument(document).blocks.some((block) =>
    block.type === "image" ? Boolean(block.image.base64) : block.segments.some((segment) => segment.text.trim().length > 0)
  );

const sanitizeFileName = (value: string) =>
  (value || "test")
    .replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s_-]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .slice(0, 80) || "test";

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const clampTableSize = (value: number) => clamp(Math.round(value), MIN_TABLE_SIZE, MAX_TABLE_SIZE);

type Point = { x: number; y: number };

const thinPoints = (points: Point[], minDist = 4): Point[] => {
  if (points.length < 2) return points;
  const result = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const last = result[result.length - 1];
    const dx = points[i].x - last.x;
    const dy = points[i].y - last.y;
    if (dx * dx + dy * dy >= minDist * minDist) result.push(points[i]);
  }
  result.push(points[points.length - 1]);
  return result;
};

const drawSmoothStroke = (ctx: CanvasRenderingContext2D, points: Point[]) => {
  if (points.length === 0) return;
  if (points.length === 1) {
    ctx.beginPath();
    ctx.arc(points[0].x, points[0].y, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  if (points.length === 2) {
    ctx.lineTo(points[1].x, points[1].y);
    ctx.stroke();
    return;
  }
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
  }
  ctx.stroke();
};
let tableTextMeasureCanvas: HTMLCanvasElement | null = null;

const getTableCellKey = (row: number, column: number) => `${row}:${column}`;

const cloneTableCellTexts = (cellTexts?: SketchTableCellTextMap | null): SketchTableCellTextMap =>
  Object.entries(cellTexts || {}).reduce<SketchTableCellTextMap>((accumulator, [key, value]) => {
    if (typeof value === "string") {
      accumulator[key] = value;
    }
    return accumulator;
  }, {});

const normalizeTableCellTexts = (
  cellTexts: SketchTableCellTextMap | null | undefined,
  rows: number,
  columns: number
): SketchTableCellTextMap =>
  Object.entries(cloneTableCellTexts(cellTexts)).reduce<SketchTableCellTextMap>((accumulator, [key, value]) => {
    const [rawRow, rawColumn] = key.split(":");
    const row = Number(rawRow);
    const column = Number(rawColumn);
    if (!Number.isInteger(row) || !Number.isInteger(column)) {
      return accumulator;
    }
    if (row < 0 || row >= rows || column < 0 || column >= columns) {
      return accumulator;
    }
    accumulator[key] = value;
    return accumulator;
  }, {});

const cloneSketchElement = (element: SketchElement): SketchElement =>
  element.type === "table"
    ? {
        ...element,
        cellTexts: cloneTableCellTexts(element.cellTexts),
      }
    : { ...element };

const cloneSketchElements = (elements: SketchElement[]) => elements.map((element) => cloneSketchElement(element));

const getTableCellSize = (table: Pick<SketchTableElement, "width" | "height" | "rows" | "columns">) => ({
  cellWidth: table.width / Math.max(table.columns, 1),
  cellHeight: table.height / Math.max(table.rows, 1),
});

const getTableCellBounds = (table: Pick<SketchTableElement, "width" | "height" | "rows" | "columns">, row: number, column: number) => {
  const { cellWidth, cellHeight } = getTableCellSize(table);
  return {
    x: column * cellWidth,
    y: row * cellHeight,
    width: cellWidth,
    height: cellHeight,
  };
};

const getTableCellContentBounds = (table: Pick<SketchTableElement, "width" | "height" | "rows" | "columns" | "strokeWidth">, row: number, column: number) => {
  const cellBounds = getTableCellBounds(table, row, column);
  const inset = Math.max(2, table.strokeWidth);

  return {
    x: cellBounds.x + inset,
    y: cellBounds.y + inset,
    width: Math.max(8, cellBounds.width - inset * 2),
    height: Math.max(8, cellBounds.height - inset * 2),
  };
};

const formatTableCellCoordinate = (
  table: Pick<SketchTableElement, "width" | "height" | "rows" | "columns" | "strokeWidth">,
  row: number,
  column: number
) => {
  const bounds = getTableCellContentBounds(table, row, column);
  return `x:${Math.round(bounds.x)} y:${Math.round(bounds.y)} • ${row + 1}. satır ${column + 1}. sütun`;
};

const getTableCellFromPointerEvent = (
  table: Pick<SketchTableElement, "width" | "height" | "rows" | "columns">,
  containerRect: DOMRect,
  clientX: number,
  clientY: number
) => {
  const relativeX = clamp(((clientX - containerRect.left) / Math.max(containerRect.width, 1)) * table.width, 0, table.width - 1);
  const relativeY = clamp(((clientY - containerRect.top) / Math.max(containerRect.height, 1)) * table.height, 0, table.height - 1);
  const { cellWidth, cellHeight } = getTableCellSize(table);

  return {
    row: clamp(Math.floor(relativeY / Math.max(cellHeight, 1)), 0, table.rows - 1),
    column: clamp(Math.floor(relativeX / Math.max(cellWidth, 1)), 0, table.columns - 1),
  };
};

const getTableCellText = (table: Pick<SketchTableElement, "cellTexts">, row: number, column: number) =>
  table.cellTexts?.[getTableCellKey(row, column)] || "";

const getTableTextMeasureContext = () => {
  if (typeof document === "undefined") {
    return null;
  }
  if (!tableTextMeasureCanvas) {
    tableTextMeasureCanvas = document.createElement("canvas");
  }
  return tableTextMeasureCanvas.getContext("2d");
};

const wrapTableCellTextLines = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
  const content = text.replace(/\r/g, "").trim();
  if (!content) {
    return [];
  }

  const wrappedLines: string[] = [];

  const pushChunkedWord = (word: string) => {
    let remaining = word;
    while (remaining.length) {
      let chunk = remaining;
      while (chunk.length > 1 && ctx.measureText(chunk).width > maxWidth) {
        chunk = chunk.slice(0, -1);
      }
      if (!chunk.trim()) {
        break;
      }
      wrappedLines.push(chunk);
      remaining = remaining.slice(chunk.length);
    }
  };

  content.split("\n").forEach((paragraph) => {
    const normalizedParagraph = paragraph.trim();
    if (!normalizedParagraph) {
      wrappedLines.push("");
      return;
    }

    const words = normalizedParagraph.split(/\s+/);
    let currentLine = "";

    words.forEach((word) => {
      const candidateLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(candidateLine).width <= maxWidth) {
        currentLine = candidateLine;
        return;
      }

      if (currentLine) {
        wrappedLines.push(currentLine);
      }

      if (ctx.measureText(word).width <= maxWidth) {
        currentLine = word;
        return;
      }

      pushChunkedWord(word);
      currentLine = "";
    });

    if (currentLine) {
      wrappedLines.push(currentLine);
    }
  });

  return wrappedLines.length ? wrappedLines : [content];
};

const getFittedTableCellTextLayout = (text: string, cellWidth: number, cellHeight: number) => {
  const ctx = getTableTextMeasureContext();
  const availableWidth = Math.max(8, cellWidth - TABLE_CELL_TEXT_PADDING_X * 2);
  const availableHeight = Math.max(8, cellHeight - TABLE_CELL_TEXT_PADDING_Y * 2);
  const normalizedText = text.replace(/\r/g, "").trim();

  if (!normalizedText) {
    const fallbackFontSize = Math.min(
      MAX_TABLE_CELL_FONT_SIZE,
      Math.max(MIN_TABLE_CELL_FONT_SIZE, Math.floor(cellHeight * 0.34))
    );
    return {
      fontSize: fallbackFontSize,
      lineHeight: Math.max(12, Math.round(fallbackFontSize * 1.25)),
      lines: [] as string[],
    };
  }

  if (!ctx) {
    const fallbackFontSize = clamp(
      Math.floor(Math.min(cellHeight * 0.34, cellWidth / Math.max(normalizedText.length * 0.55, 1))),
      MIN_TABLE_CELL_FONT_SIZE,
      MAX_TABLE_CELL_FONT_SIZE
    );
    return {
      fontSize: fallbackFontSize,
      lineHeight: Math.max(12, Math.round(fallbackFontSize * 1.25)),
      lines: normalizedText.split("\n"),
    };
  }

  for (let fontSize = Math.min(MAX_TABLE_CELL_FONT_SIZE, Math.floor(cellHeight * 0.42)); fontSize >= MIN_TABLE_CELL_FONT_SIZE; fontSize -= 1) {
    ctx.font = `500 ${fontSize}px Arial`;
    const lines = wrapTableCellTextLines(ctx, normalizedText, availableWidth);
    const lineHeight = Math.max(12, Math.round(fontSize * 1.25));
    const totalHeight = lines.length * lineHeight;
    const widestLine = lines.reduce((maxWidthSoFar, line) => Math.max(maxWidthSoFar, ctx.measureText(line || " ").width), 0);

    if (totalHeight <= availableHeight && widestLine <= availableWidth) {
      return {
        fontSize,
        lineHeight,
        lines,
      };
    }
  }

  ctx.font = `500 ${MIN_TABLE_CELL_FONT_SIZE}px Arial`;
  return {
    fontSize: MIN_TABLE_CELL_FONT_SIZE,
    lineHeight: Math.max(12, Math.round(MIN_TABLE_CELL_FONT_SIZE * 1.25)),
    lines: wrapTableCellTextLines(ctx, normalizedText, availableWidth),
  };
};

const normalizeSketchElement = (element: SketchElement): SketchElement => {
  if (element.type !== "table") {
    return cloneSketchElement(element);
  }

  const rows = clampTableSize(element.rows);
  const columns = clampTableSize(element.columns);

  return {
    ...element,
    rows,
    columns,
    cellTexts: normalizeTableCellTexts(element.cellTexts, rows, columns),
  };
};

const normalizeElementBounds = (startX: number, startY: number, currentX: number, currentY: number, tool: SketchPlacementTool): SketchDraftElement => {
  const dx = currentX - startX;
  const dy = currentY - startY;

  if (tool === "square") {
    const size = Math.max(Math.abs(dx), Math.abs(dy), MIN_ELEMENT_SIZE);
    return {
      type: tool,
      x: dx >= 0 ? startX : startX - size,
      y: dy >= 0 ? startY : startY - size,
      width: size,
      height: size,
    };
  }

  const width = Math.max(Math.abs(dx), tool === "text" ? DEFAULT_TEXTBOX_WIDTH : MIN_ELEMENT_SIZE);
  const height = Math.max(Math.abs(dy), tool === "text" ? DEFAULT_TEXTBOX_HEIGHT : MIN_ELEMENT_SIZE);

  return {
    type: tool,
    x: dx >= 0 ? startX : startX - width,
    y: dy >= 0 ? startY : startY - height,
    width,
    height,
  };
};

const hasRenderableSketchElements = (elements: SketchElement[]) =>
  elements.some((element) =>
    element.type === "text"
      ? element.text.trim().length > 0
      : element.width >= MIN_ELEMENT_SIZE && element.height >= MIN_ELEMENT_SIZE
  );

export default function InDesignQuestionBank() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert, showConfirm, showPrompt, showToast } = useModal();

  const [layoutSettingsOpen, setLayoutSettingsOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState({ test_adi: "", kategori: "", soru_sayisi: 10 });

  const [state, setState] = useState<QuestionBankState | null>(null);
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);
  const [draggedQuestionId, setDraggedQuestionId] = useState<string | null>(null);

  const [imagePrompt, setImagePrompt] = useState("");
  const [imageInputMode, setImageInputMode] = useState<"text" | "draw" | "upload">("text");
  const [imageGenerating, setImageGenerating] = useState(false);
  const [sketchGenerating, setSketchGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [sketchTool, setSketchTool] = useState<SketchTool>("pen");
  const [shapePickerOpen, setShapePickerOpen] = useState(false);
  const [sketchColor, setSketchColor] = useState("#111827");
  const [sketchFillColor, setSketchFillColor] = useState("#ffffff");
  const [sketchLineWidth, setSketchLineWidth] = useState(3);
  const [sketchTextSize, setSketchTextSize] = useState(18);
  const [sketchViewportScale, setSketchViewportScale] = useState({ x: 1, y: 1 });
  const [sketchHasDrawing, setSketchHasDrawing] = useState(false);
  const [sketchHistorySize, setSketchHistorySize] = useState(0);
  const [isEraser, setIsEraser] = useState(false);
  const [sketchElements, setSketchElements] = useState<SketchElement[]>([]);
  const [draftSketchElement, setDraftSketchElement] = useState<SketchDraftElement | null>(null);
  const [selectedSketchElementId, setSelectedSketchElementId] = useState<string | null>(null);
  const [activeTextEditId, setActiveTextEditId] = useState<string | null>(null);
  const [activeTableCellEdit, setActiveTableCellEdit] = useState<ActiveTableCellEdit>(null);
  const [pendingDroppedItem, setPendingDroppedItem] = useState<SketchPlacementTool | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isCorrectingText, setIsCorrectingText] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [richImageUploadTarget, setRichImageUploadTarget] = useState<ChoiceKey | null>(null);
  const [choiceImageModalOpen, setChoiceImageModalOpen] = useState(false);
  const [choiceImageTarget, setChoiceImageTarget] = useState<ChoiceKey | null>(null);
  const [choiceImageInputMode, setChoiceImageInputMode] = useState<"text" | "draw" | "upload">("text");
  const [choiceImagePrompt, setChoiceImagePrompt] = useState("");
  const [choiceImageGenerating, setChoiceImageGenerating] = useState(false);
  const [choiceSketchGenerating, setChoiceSketchGenerating] = useState(false);
  const [choiceIsSketchDrawing, setChoiceIsSketchDrawing] = useState(false);
  const [choiceSketchHasDrawing, setChoiceSketchHasDrawing] = useState(false);
  const [choiceSketchColor, setChoiceSketchColor] = useState("#111827");
  const [choiceSketchLineWidth, setChoiceSketchLineWidth] = useState(3);
  const [choiceSketchIsEraser, setChoiceSketchIsEraser] = useState(false);
  const [choiceSketchUndoSize, setChoiceSketchUndoSize] = useState(0);
  const [choiceReferenceImage, setChoiceReferenceImage] = useState<{
    mimeType: string;
    base64: string;
    fileName?: string;
  } | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const introTextRef = useRef<HTMLTextAreaElement | null>(null);
  const questionEditorRef = useRef<RichContentEditorHandle | null>(null);
  const choiceEditorRefs = useRef<Record<ChoiceKey, RichContentEditorHandle | null>>({
    A: null,
    B: null,
    C: null,
    D: null,
    E: null,
  });
  const richImageInputRef = useRef<HTMLInputElement | null>(null);
  const mathPickerButtonRefs = useRef<Record<MathTargetField, HTMLButtonElement | null>>({
    question: null,
    A: null,
    B: null,
    C: null,
    D: null,
    E: null,
  });
  const mathPickerSurfaceRef = useRef<HTMLDivElement | null>(null);
  const mathPickerPlacementRef = useRef<MathPickerPlacement | null>(null);
  const referenceImageInputRef = useRef<HTMLInputElement | null>(null);
  const sketchBoardRef = useRef<HTMLDivElement | null>(null);
  const sketchControlsRef = useRef<HTMLDivElement | null>(null);
  const sketchCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const sketchElementsRef = useRef<SketchElement[]>([]);
  const sketchUndoStackRef = useRef<SketchHistoryEntry[]>([]);
  const blankSketchSnapshotRef = useRef("");
  const sketchInteractionRef = useRef<{
    mode: "create" | "resize" | "move" | null;
    pointerId: number | null;
    startX: number;
    startY: number;
    elementId: string | null;
    createType: SketchPlacementTool | null;
    originX: number;
    originY: number;
    originWidth: number;
    originHeight: number;
  }>({
    mode: null,
    pointerId: null,
    startX: 0,
    startY: 0,
    elementId: null,
    createType: null,
    originX: 0,
    originY: 0,
    originWidth: 0,
    originHeight: 0,
  });
  const skipNextSketchReloadRef = useRef(false);
  const sketchLastPointRef = useRef<Point | null>(null);
  const sketchRawPointsRef = useRef<Point[]>([]);
  const strokeStartSnapshotRef = useRef<string>("");
  const choiceSketchCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const choiceSketchLastPointRef = useRef<Point | null>(null);
  const choiceSketchRawPointsRef = useRef<Point[]>([]);
  const choiceStrokeStartSnapshotRef = useRef<string>("");
  const choiceSketchUndoStackRef = useRef<string[]>([]);
  const choiceBlankSketchSnapshotRef = useRef("");
  const choiceReferenceImageInputRef = useRef<HTMLInputElement | null>(null);
  const hydratedRef = useRef(false);
  const isMountedRef = useRef(true);
  const questionBankIdRef = useRef<string | undefined>(id);
  const skipNextAutosaveRef = useRef(false);
  const saveTimeoutRef = useRef<number | null>(null);
  const pendingStateRef = useRef<QuestionBankState | null>(null);
  const stateRef = useRef<QuestionBankState | null>(null);
  const [openMathPicker, setOpenMathPicker] = useState<MathPickerTargetState>(null);
  const [mathPickerPosition, setMathPickerPosition] = useState<MathPickerPosition>(null);

  const activeQuestion = useMemo(
    () => state?.sorular.find((q) => q.id === activeQuestionId) || null,
    [state, activeQuestionId]
  );
  const previewQuestionIndex = useMemo(() => {
    if (!state?.sorular?.length || !activeQuestionId) {
      return -1;
    }
    return state.sorular.findIndex((question) => question.id === activeQuestionId);
  }, [state, activeQuestionId]);
  const previewQuestionCount = state?.sorular.length || 0;
  const remainingPreviewQuestionCount =
    previewQuestionIndex >= 0 && previewQuestionCount > 0 ? Math.max(0, previewQuestionCount - (previewQuestionIndex + 1)) : 0;
  const activeChoiceKeys = useMemo(
    () => (activeQuestion ? getVisibleChoiceKeys(activeQuestion.secenek_sayisi) : []),
    [activeQuestion]
  );
  const activeQuestionImageContext = useMemo(() => {
    if (!activeQuestion) return undefined;

    return {
      testName: state?.test_adi || setupData.test_adi || "",
      subject: state?.kategori || setupData.kategori || "",
      questionNumber: activeQuestion.sira,
      introText: activeQuestion.intro_text || "",
      questionText: activeQuestion.soru_metni || "",
      options: getVisibleChoiceKeys(activeQuestion.secenek_sayisi).map((key) => ({
        key,
        text: extractPlainTextFromRichContent(getChoiceContent(activeQuestion, key)),
      })),
      correctAnswer: activeQuestion.dogru_cevap,
    };
  }, [activeQuestion, setupData.kategori, setupData.test_adi, state?.kategori, state?.test_adi]);
  const activeTemplateOption = useMemo(
    () => (activeQuestion ? getQuestionTemplateOption(activeQuestion.templateType) : null),
    [activeQuestion]
  );
  const activeQuestionLayoutSummary = useMemo(() => {
    if (!activeQuestion) return "";
    const parts = [activeQuestion.templateType === "image_with_caption" ? "Görselli" : "Standart"];
    parts.push(
      activeQuestion.choiceLayout === "horizontal"
        ? "Yatay şıklı"
        : activeQuestion.choiceLayout === "staggered"
          ? "Çapraz şıklı"
          : "Alt alta şıklı"
    );
    return parts.join(" • ");
  }, [activeQuestion]);
  const resolvedChoiceLayout = activeQuestion?.choiceLayout || state?.sorular[0]?.choiceLayout || "vertical";
  const resolvedShowIntroText = Boolean(activeQuestion?.showIntroText);
  const sketchHasRenderableContent = sketchHasDrawing || hasRenderableSketchElements(sketchElements);
  const selectedSketchElement = useMemo(
    () => sketchElements.find((element) => element.id === selectedSketchElementId) || null,
    [sketchElements, selectedSketchElementId]
  );
  const selectedSketchElementLabel = selectedSketchElement
    ? selectedSketchElement.type === "text"
      ? "Metin Kutusu"
      : selectedSketchElement.type === "table"
        ? "Tablo"
      : selectedSketchElement.type === "rectangle"
        ? "Dikdörtgen"
        : selectedSketchElement.type === "square"
          ? "Kare"
          : "Üçgen"
    : null;

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    questionBankIdRef.current = id;
  }, [id]);

  useEffect(() => {
    return () => {
      const pendingState = pendingStateRef.current;
      clearPendingSave();
      pendingStateRef.current = null;
      isMountedRef.current = false;

      if (pendingState) {
        void persistState(pendingState, { updateIndicators: false }).catch((error) => {
          console.error("Question bank save failed during unmount:", error);
        });
      }
    };
  }, []);

  useEffect(() => {
    setImagePrompt(activeQuestion?.referenceImage ? "Bu referans görseli gerçek hayata uyarla." : "");
  }, [activeQuestion?.id, imageInputMode]);

  useEffect(() => {
    if (activeQuestion?.referenceImage) {
      setImageInputMode("upload");
    }
  }, [activeQuestion?.id]);

  useEffect(() => {
    if (!choiceImageModalOpen) return;
    const basePrompt =
      choiceImageTarget && activeQuestion
        ? extractPlainTextFromRichContent(getChoiceContent(activeQuestion, choiceImageTarget))
        : "";
    setChoiceImagePrompt(basePrompt);
  }, [choiceImageModalOpen, choiceImageTarget, activeQuestion?.id]);

  useEffect(() => {
    sketchElementsRef.current = sketchElements;
  }, [sketchElements]);

  useEffect(() => {
    setSketchElements(cloneSketchElements(activeQuestion?.sketchElements || []));
    setDraftSketchElement(null);
    setSelectedSketchElementId(null);
    setActiveTextEditId(null);
    setActiveTableCellEdit(null);
    setShapePickerOpen(false);
    setPendingDroppedItem(null);
  }, [activeQuestion?.id]);

  useEffect(() => {
    if (!imageModalOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (sketchBoardRef.current?.contains(target)) return;
      if (sketchControlsRef.current?.contains(target)) return;
      clearSketchSelection();
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [imageModalOpen]);

  useEffect(() => {
    if (!imageModalOpen) return;
    const board = sketchBoardRef.current;
    if (!board) return;

    const updateScale = () => {
      const rect = board.getBoundingClientRect();
      setSketchViewportScale({
        x: rect.width > 0 ? rect.width / SKETCH_CANVAS_WIDTH : 1,
        y: rect.height > 0 ? rect.height / SKETCH_CANVAS_HEIGHT : 1,
      });
    };

    updateScale();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateScale) : null;
    observer?.observe(board);
    window.addEventListener("resize", updateScale);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [imageModalOpen]);

  useEffect(() => {
    if (!choiceImageModalOpen) return;
    if (choiceImageInputMode !== "draw") return;
    const canvas = choiceSketchCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (choiceSketchHasDrawing) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    choiceBlankSketchSnapshotRef.current = canvas.toDataURL("image/png");
  }, [choiceImageModalOpen, choiceImageInputMode, choiceSketchHasDrawing]);

  useEffect(() => {
    if (!activeQuestionId) return;
    setOpenMathPicker(null);
    setMathPickerPosition(null);
    mathPickerPlacementRef.current = null;
  }, [activeQuestionId]);

  useEffect(() => {
    if (!openMathPicker) return;

    const updatePosition = () => {
      const button = mathPickerButtonRefs.current[openMathPicker.field];
      if (!button) return;

      const inputAnchor = button;
      const anchor = inputAnchor || button;
      const anchorRect = anchor.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const isChoiceField = openMathPicker.field !== "question";
      const isQuestionField = openMathPicker.field === "question";
      const verticalAnchorRect = isChoiceField ? buttonRect : anchorRect;

      if (anchorRect.bottom < 0 || anchorRect.top > window.innerHeight) {
        setOpenMathPicker(null);
        setMathPickerPosition(null);
        mathPickerPlacementRef.current = null;
        return;
      }

      const measuredWidth = mathPickerSurfaceRef.current?.offsetWidth || 520;
      const pickerWidth = Math.min(window.innerWidth - 24, measuredWidth);
      const preferredHeight = mathPickerSurfaceRef.current?.offsetHeight || 430;
      const spaceAbove = Math.max(0, verticalAnchorRect.top - 12);
      const spaceBelow = Math.max(0, window.innerHeight - buttonRect.bottom - 12);
      const placement =
        mathPickerPlacementRef.current ||
        (isChoiceField
          ? spaceAbove >= 140
            ? "above"
            : "below"
          : isQuestionField
            ? spaceAbove >= 140
              ? "above"
              : "below"
            : spaceBelow >= 140
              ? "below"
              : spaceAbove >= 140
                ? "above"
                : spaceBelow >= spaceAbove
                  ? "below"
                  : "above");
      mathPickerPlacementRef.current = placement;
      const placeBelow = placement === "below";
      const availableSpace = placeBelow ? spaceBelow : spaceAbove;
      const maxHeight = Math.min(430, Math.max(140, availableSpace - 10));
      const effectiveHeight = Math.min(preferredHeight, maxHeight);
      const anchorCenter = anchorRect.left + anchorRect.width / 2;
      const left = Math.min(Math.max(12, anchorCenter - pickerWidth / 2), Math.max(12, window.innerWidth - pickerWidth - 12));
      const top = placeBelow
        ? Math.min(buttonRect.bottom + 6, Math.max(12, window.innerHeight - effectiveHeight - 12))
        : Math.max(12, verticalAnchorRect.top - effectiveHeight + (isChoiceField ? 28 : -6));

      setMathPickerPosition({ top, left, maxHeight });
    };

    updatePosition();
    const rafId = window.requestAnimationFrame(updatePosition);

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const button = mathPickerButtonRefs.current[openMathPicker.field];
      if (target && mathPickerSurfaceRef.current?.contains(target)) return;
      if (target && button?.contains(target)) return;
      setOpenMathPicker(null);
      setMathPickerPosition(null);
      mathPickerPlacementRef.current = null;
    };

    const handleViewportChange = () => updatePosition();

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", handleViewportChange, true);
    window.addEventListener("resize", handleViewportChange);
    return () => {
      window.cancelAnimationFrame(rafId);
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", handleViewportChange, true);
      window.removeEventListener("resize", handleViewportChange);
    };
  }, [openMathPicker]);

  useEffect(() => {
    if (!previewModalOpen) return;

    const handlePreviewNavigation = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPreviewQuestion("previous");
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToPreviewQuestion("next");
      }
    };

    window.addEventListener("keydown", handlePreviewNavigation);
    return () => window.removeEventListener("keydown", handlePreviewNavigation);
  }, [previewModalOpen, previewQuestionIndex, state]);

  useEffect(() => {
    const loadQuestionBank = async () => {
      if (!id) {
        setLoadError("Dizgici kimliği bulunamadı.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);
      try {
        const draft = await ApiService.getQuestionBank(id);
        const content = {
          ...draft.content,
          sorular: Array.isArray(draft.content?.sorular) ? draft.content.sorular.map((question) => normalizeQuestionForEditor(question)) : [],
        };
        skipNextAutosaveRef.current = true;
        pendingStateRef.current = null;
        setState(content);
        setSetupData({
          test_adi: content.test_adi || draft.testAdi,
          kategori: content.kategori || draft.kategori || "",
          soru_sayisi: content.sorular?.length || draft.questionCount || 0,
        });
        setActiveQuestionId(content.sorular?.[0]?.id || null);
        setLastSavedAt(draft.updatedAt || null);
        hydratedRef.current = true;
      } catch (error: any) {
        setLoadError(error?.message || "Dizgici yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };

    loadQuestionBank();
  }, [id]);

  useEffect(() => {
    if (!hydratedRef.current || !state || !id) return;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      pendingStateRef.current = null;
      clearPendingSave();
      return;
    }

    pendingStateRef.current = state;
    clearPendingSave();
    saveTimeoutRef.current = window.setTimeout(async () => {
      const pendingState = pendingStateRef.current;
      saveTimeoutRef.current = null;
      if (!pendingState) {
        return;
      }
      pendingStateRef.current = null;
      try {
        await persistState(pendingState);
      } catch (error) {
        pendingStateRef.current = pendingState;
        console.error("Question bank save failed:", error);
      }
    }, 700);

    return clearPendingSave;
  }, [state, id]);

  const paintSketchBackground = () => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  };

  const getBlankSketchSnapshot = () => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return "";
    return canvas.toDataURL("image/png");
  };

  const syncSketchHistoryState = () => {
    setSketchHistorySize(sketchUndoStackRef.current.length);
  };

  const syncSketchQuestionData = (snapshot: string | null, elements: SketchElement[]) => {
    updateActiveQuestion((q) => ({
      ...q,
      sketchBase64: snapshot,
      sketchElements: elements.length ? cloneSketchElements(elements) : null,
    }));
  };

  const drawSketchElementToContext = (ctx: CanvasRenderingContext2D, element: SketchElement) => {
    ctx.save();
    ctx.lineWidth = element.strokeWidth;
    ctx.strokeStyle = element.strokeColor;
    ctx.fillStyle = element.fillColor || "#ffffff";

    if (element.type === "text") {
      ctx.fillStyle = element.textColor;
      ctx.font = `${element.fontSize}px Arial`;
      ctx.textBaseline = "top";
      const lines = element.text.split("\n");
      const lineHeight = Math.round(element.fontSize * 1.35);
      const paddingX = 8;
      const paddingY = 6;
      lines.forEach((line, index) => {
        ctx.fillText(line || " ", element.x + paddingX, element.y + paddingY + index * lineHeight, Math.max(0, element.width - paddingX * 2));
      });
      ctx.restore();
      return;
    }

    if (element.type === "triangle") {
      ctx.beginPath();
      ctx.moveTo(element.x + element.width / 2, element.y);
      ctx.lineTo(element.x + element.width, element.y + element.height);
      ctx.lineTo(element.x, element.y + element.height);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (element.type === "circle") {
      ctx.beginPath();
      ctx.ellipse(
        element.x + element.width / 2,
        element.y + element.height / 2,
        element.width / 2,
        element.height / 2,
        0,
        0,
        Math.PI * 2
      );
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (element.type === "table") {
      const { cellWidth, cellHeight } = getTableCellSize(element);
      ctx.fillRect(element.x, element.y, element.width, element.height);
      ctx.strokeRect(element.x, element.y, element.width, element.height);

      for (let row = 1; row < element.rows; row += 1) {
        const lineY = element.y + cellHeight * row;
        ctx.beginPath();
        ctx.moveTo(element.x, lineY);
        ctx.lineTo(element.x + element.width, lineY);
        ctx.stroke();
      }

      for (let column = 1; column < element.columns; column += 1) {
        const lineX = element.x + cellWidth * column;
        ctx.beginPath();
        ctx.moveTo(lineX, element.y);
        ctx.lineTo(lineX, element.y + element.height);
        ctx.stroke();
      }

      ctx.fillStyle = element.strokeColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      for (let row = 0; row < element.rows; row += 1) {
        for (let column = 0; column < element.columns; column += 1) {
          const cellText = getTableCellText(element, row, column);
          if (!cellText.trim()) {
            continue;
          }

          const cellBounds = getTableCellBounds(element, row, column);
          const textLayout = getFittedTableCellTextLayout(cellText, cellBounds.width, cellBounds.height);
          if (!textLayout.lines.length) {
            continue;
          }

          ctx.font = `500 ${textLayout.fontSize}px Arial`;
          const startY =
            element.y +
            cellBounds.y +
            cellBounds.height / 2 -
            ((textLayout.lines.length - 1) * textLayout.lineHeight) / 2;

          textLayout.lines.forEach((line, index) => {
            ctx.fillText(
              line || " ",
              element.x + cellBounds.x + cellBounds.width / 2,
              startY + index * textLayout.lineHeight,
              Math.max(0, cellBounds.width - TABLE_CELL_TEXT_PADDING_X * 2)
            );
          });
        }
      }
      ctx.restore();
      return;
    }

    ctx.fillRect(element.x, element.y, element.width, element.height);
    ctx.strokeRect(element.x, element.y, element.width, element.height);
    ctx.restore();
  };

  const exportSketchCompositeDataUrl = () => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return "";
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const exportCtx = exportCanvas.getContext("2d");
    if (!exportCtx) return "";
    exportCtx.fillStyle = "#ffffff";
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportCtx.drawImage(canvas, 0, 0, exportCanvas.width, exportCanvas.height);
    sketchElementsRef.current.forEach((element) => drawSketchElementToContext(exportCtx, element));
    return exportCanvas.toDataURL("image/png");
  };

  const pushCurrentSketchHistory = () => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return;
    const snapshot = canvas.toDataURL("image/png");
    const elements = cloneSketchElements(sketchElementsRef.current);
    const stack = sketchUndoStackRef.current;
    const last = stack[stack.length - 1];
    if (last && last.snapshot === snapshot && JSON.stringify(last.elements) === JSON.stringify(elements)) {
      return;
    }
    sketchUndoStackRef.current = [...stack.slice(-19), { snapshot, elements }];
    syncSketchHistoryState();
  };

  const applySketchSnapshot = (snapshot: string, elements: SketchElement[] = [], persist = true) => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const blankSnapshot = blankSketchSnapshotRef.current || getBlankSketchSnapshot();
    if (!snapshot || snapshot === blankSnapshot) {
      paintSketchBackground();
      setSketchHasDrawing(false);
      setSketchElements(cloneSketchElements(elements));
      setDraftSketchElement(null);
      if (persist) {
        skipNextSketchReloadRef.current = true;
        syncSketchQuestionData(null, elements);
      }
      return;
    }

    const img = new Image();
    img.onload = () => {
      paintSketchBackground();
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setSketchHasDrawing(true);
      setSketchElements(cloneSketchElements(elements));
      setDraftSketchElement(null);
      if (persist) {
        skipNextSketchReloadRef.current = true;
        syncSketchQuestionData(snapshot, elements);
      }
    };
    img.src = snapshot;
  };

  const loadSketchToCanvas = (dataUrl?: string | null) => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return;
    paintSketchBackground();
    const blankSnapshot = getBlankSketchSnapshot();
    blankSketchSnapshotRef.current = blankSnapshot;

    if (!dataUrl) {
      setSketchHasDrawing(false);
      setSketchElements(cloneSketchElements(activeQuestion?.sketchElements || []));
      sketchUndoStackRef.current = [];
      syncSketchHistoryState();
      return;
    }
    sketchUndoStackRef.current = [];
    syncSketchHistoryState();
    applySketchSnapshot(dataUrl, activeQuestion?.sketchElements || [], false);
  };

  useEffect(() => {
    if (skipNextSketchReloadRef.current) {
      skipNextSketchReloadRef.current = false;
      return;
    }
    loadSketchToCanvas(activeQuestion?.sketchBase64 || null);
  }, [activeQuestion?.id, activeQuestion?.sketchBase64]);

  const saveSketchToActiveQuestion = () => {
    const canvas = sketchCanvasRef.current;
    if (!canvas || !activeQuestion) return;
    const dataUrl = canvas.toDataURL("image/png");
    skipNextSketchReloadRef.current = true;
    const blankSnapshot = blankSketchSnapshotRef.current || getBlankSketchSnapshot();
    syncSketchQuestionData(dataUrl === blankSnapshot ? null : dataUrl, sketchElements);
  };

  const getSketchPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const getSketchPointFromClient = (clientX: number, clientY: number) => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const requestTableDimensions = async () => {
    const rowsValue = await showPrompt({
      title: "Tablo Satır Sayısı",
      message: `Kaç satır olsun? (${MIN_TABLE_SIZE}-${MAX_TABLE_SIZE})`,
      placeholder: "Örn: 3",
      defaultValue: "3",
      confirmText: "Devam",
      cancelText: "Vazgeç",
    });
    if (!rowsValue.trim()) {
      return null;
    }

    const columnsValue = await showPrompt({
      title: "Tablo Sütun Sayısı",
      message: `Kaç sütun olsun? (${MIN_TABLE_SIZE}-${MAX_TABLE_SIZE})`,
      placeholder: "Örn: 4",
      defaultValue: "4",
      confirmText: "Oluştur",
      cancelText: "Vazgeç",
    });
    if (!columnsValue.trim()) {
      return null;
    }

    const rows = Number(rowsValue);
    const columns = Number(columnsValue);
    if (!Number.isInteger(rows) || !Number.isInteger(columns)) {
      await showAlert({
        title: "Geçersiz Tablo",
        message: "Satır ve sütun sayıları tam sayı olmalıdır.",
        type: "warning",
      });
      return null;
    }

    return {
      rows: clampTableSize(rows),
      columns: clampTableSize(columns),
    };
  };

  const createSketchElement = (
    type: SketchPlacementTool,
    x: number,
    y: number,
    options?: {
      rows?: number;
      columns?: number;
    }
  ): SketchElement =>
    type === "text"
      ? {
          id: crypto.randomUUID(),
          type: "text",
          x: clamp(x, 0, SKETCH_CANVAS_WIDTH - DEFAULT_TEXTBOX_WIDTH),
          y: clamp(y, 0, SKETCH_CANVAS_HEIGHT - DEFAULT_TEXTBOX_HEIGHT),
          width: DEFAULT_TEXTBOX_WIDTH,
          height: DEFAULT_TEXTBOX_HEIGHT,
          text: "",
          textColor: sketchColor,
          fontSize: sketchTextSize,
          strokeColor: "#94a3b8",
          strokeWidth: 1,
          fillColor: "#ffffff",
        }
      : type === "table"
        ? {
            id: crypto.randomUUID(),
            type: "table",
            x: clamp(x, 0, SKETCH_CANVAS_WIDTH - DEFAULT_TABLE_CELL_WIDTH * clampTableSize(options?.columns || 4)),
            y: clamp(y, 0, SKETCH_CANVAS_HEIGHT - DEFAULT_TABLE_CELL_HEIGHT * clampTableSize(options?.rows || 3)),
            width: DEFAULT_TABLE_CELL_WIDTH * clampTableSize(options?.columns || 4),
            height: DEFAULT_TABLE_CELL_HEIGHT * clampTableSize(options?.rows || 3),
            rows: clampTableSize(options?.rows || 3),
            columns: clampTableSize(options?.columns || 4),
            cellTexts: {},
            strokeColor: sketchColor,
            strokeWidth: Math.max(1, sketchLineWidth),
            fillColor: "#ffffff",
          }
      : {
          id: crypto.randomUUID(),
          type,
          x: clamp(x, 0, SKETCH_CANVAS_WIDTH - DEFAULT_SHAPE_SIZE),
          y: clamp(y, 0, SKETCH_CANVAS_HEIGHT - DEFAULT_SHAPE_SIZE),
          width: DEFAULT_SHAPE_SIZE,
          height: DEFAULT_SHAPE_SIZE,
          strokeColor: sketchColor,
          strokeWidth: Math.max(1, sketchLineWidth),
          fillColor: sketchFillColor,
        };

  const createSketchDragPreview = (type: SketchPlacementTool) => {
    const preview = document.createElement("div");
    preview.style.position = "absolute";
    preview.style.top = "-9999px";
    preview.style.left = "-9999px";
    preview.style.pointerEvents = "none";
    preview.style.background = "transparent";

    if (type === "text") {
      preview.style.width = "180px";
      preview.style.height = "64px";
      preview.style.border = "2px dashed #64748b";
      preview.style.borderRadius = "8px";
      preview.style.background = "rgba(255,255,255,0.96)";
      preview.style.boxSizing = "border-box";
      preview.style.padding = "10px 12px";
      preview.style.color = "#94a3b8";
      preview.style.font = "14px Arial";
      preview.textContent = "Metin kutusu";
      return preview;
    }

    if (type === "table") {
      const svgNS = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(svgNS, "svg");
      svg.setAttribute("width", "132");
      svg.setAttribute("height", "96");
      svg.setAttribute("viewBox", "0 0 132 96");

      const outer = document.createElementNS(svgNS, "rect");
      outer.setAttribute("x", "8");
      outer.setAttribute("y", "8");
      outer.setAttribute("width", "116");
      outer.setAttribute("height", "80");
      outer.setAttribute("fill", "#ffffff");
      outer.setAttribute("stroke", sketchColor);
      outer.setAttribute("stroke-width", "4");
      svg.appendChild(outer);

      [1, 2].forEach((index) => {
        const line = document.createElementNS(svgNS, "line");
        line.setAttribute("x1", String(8 + (116 / 3) * index));
        line.setAttribute("y1", "8");
        line.setAttribute("x2", String(8 + (116 / 3) * index));
        line.setAttribute("y2", "88");
        line.setAttribute("stroke", sketchColor);
        line.setAttribute("stroke-width", "3");
        svg.appendChild(line);
      });
      [1].forEach((index) => {
        const line = document.createElementNS(svgNS, "line");
        line.setAttribute("x1", "8");
        line.setAttribute("y1", String(8 + (80 / 2) * index));
        line.setAttribute("x2", "124");
        line.setAttribute("y2", String(8 + (80 / 2) * index));
        line.setAttribute("stroke", sketchColor);
        line.setAttribute("stroke-width", "3");
        svg.appendChild(line);
      });

      preview.appendChild(svg);
      return preview;
    }

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("width", "96");
    svg.setAttribute("height", "96");
    svg.setAttribute("viewBox", "0 0 96 96");

    if (type === "triangle") {
      const polygon = document.createElementNS(svgNS, "polygon");
      polygon.setAttribute("points", "48,10 86,82 10,82");
      polygon.setAttribute("fill", "#ffffff");
      polygon.setAttribute("stroke", sketchColor);
      polygon.setAttribute("stroke-width", "4");
      svg.appendChild(polygon);
    } else if (type === "circle") {
      const ellipse = document.createElementNS(svgNS, "ellipse");
      ellipse.setAttribute("cx", "48");
      ellipse.setAttribute("cy", "48");
      ellipse.setAttribute("rx", "38");
      ellipse.setAttribute("ry", "38");
      ellipse.setAttribute("fill", sketchFillColor);
      ellipse.setAttribute("stroke", sketchColor);
      ellipse.setAttribute("stroke-width", "4");
      svg.appendChild(ellipse);
    } else {
      const rect = document.createElementNS(svgNS, "rect");
      rect.setAttribute("x", "10");
      rect.setAttribute("y", type === "rectangle" ? "22" : "10");
      rect.setAttribute("width", type === "rectangle" ? "76" : "76");
      rect.setAttribute("height", type === "rectangle" ? "52" : "76");
      rect.setAttribute("fill", sketchFillColor);
      rect.setAttribute("stroke", sketchColor);
      rect.setAttribute("stroke-width", "4");
      svg.appendChild(rect);
    }

    preview.appendChild(svg);
    return preview;
  };

  const handleSketchPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return;
    const { x, y } = getSketchPoint(event);
    const hadSelection = Boolean(selectedSketchElementId || activeTextEditId || activeTableCellEdit);
    clearSketchSelection();

    if (hadSelection) {
      return;
    }

    if (sketchTool !== "pen") {
      pushCurrentSketchHistory();
      const draft = normalizeElementBounds(x, y, x, y, sketchTool);
      setDraftSketchElement(draft);
      sketchInteractionRef.current = {
        mode: "create",
        pointerId: event.pointerId,
        startX: x,
        startY: y,
        elementId: null,
        createType: sketchTool,
        originX: 0,
        originY: 0,
        originWidth: 0,
        originHeight: 0,
      };
      canvas.setPointerCapture(event.pointerId);
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    pushCurrentSketchHistory();
    setIsDrawing(true);
    canvas.setPointerCapture(event.pointerId);

    strokeStartSnapshotRef.current = canvas.toDataURL("image/png");
    sketchRawPointsRef.current = [{ x, y }];
    sketchLastPointRef.current = { x, y };

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = sketchLineWidth;
    ctx.strokeStyle = isEraser ? "#ffffff" : sketchColor;
    ctx.globalCompositeOperation = isEraser ? "destination-out" : "source-over";
  };

  const handleSketchPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (sketchInteractionRef.current.mode === "create" && draftSketchElement) {
      const { x, y } = getSketchPoint(event);
      setDraftSketchElement(
        normalizeElementBounds(
          sketchInteractionRef.current.startX,
          sketchInteractionRef.current.startY,
          x,
          y,
          sketchInteractionRef.current.createType || "text"
        )
      );
      return;
    }

    if (!isDrawing) return;
    const canvas = sketchCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { x, y } = getSketchPoint(event);
    sketchRawPointsRef.current.push({ x, y });
    const prev = sketchLastPointRef.current;
    if (prev) {
      const midX = (prev.x + x) / 2;
      const midY = (prev.y + y) / 2;
      ctx.quadraticCurveTo(prev.x, prev.y, midX, midY);
    } else {
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    sketchLastPointRef.current = { x, y };
    setSketchHasDrawing(true);
  };

  const handleSketchPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = sketchCanvasRef.current;
    if (!canvas) return;

    if (sketchInteractionRef.current.mode === "create" && draftSketchElement) {
      const finalized = draftSketchElement;
      const nextElement = createSketchElement(finalized.type, finalized.x, finalized.y);
      nextElement.width = clamp(finalized.width || nextElement.width, MIN_ELEMENT_SIZE, SKETCH_CANVAS_WIDTH);
      nextElement.height = clamp(finalized.height || nextElement.height, MIN_ELEMENT_SIZE, SKETCH_CANVAS_HEIGHT);

      const nextElements = [...sketchElements, nextElement];
      setSketchElements(nextElements);
      setSelectedSketchElementId(nextElement.id);
      setActiveTextEditId(nextElement.type === "text" ? nextElement.id : null);
      setDraftSketchElement(null);
      sketchInteractionRef.current.mode = null;
      sketchInteractionRef.current.pointerId = null;
      sketchInteractionRef.current.createType = null;
      canvas.releasePointerCapture(event.pointerId);
      skipNextSketchReloadRef.current = true;
      syncSketchQuestionData(activeQuestion?.sketchBase64 || null, nextElements);
      return;
    }

    if (!isDrawing) return;
    const rawPoints = sketchRawPointsRef.current;
    sketchRawPointsRef.current = [];
    sketchLastPointRef.current = null;
    canvas.releasePointerCapture(event.pointerId);
    setIsDrawing(false);

    const snapshot = strokeStartSnapshotRef.current;
    const capturedLineWidth = sketchLineWidth;
    const capturedColor = sketchColor;
    const capturedIsEraser = isEraser;

    if (snapshot && rawPoints.length > 1) {
      const img = new Image();
      img.onload = () => {
        const c = sketchCanvasRef.current;
        if (!c) return;
        const ctx2 = c.getContext("2d");
        if (!ctx2) return;
        ctx2.clearRect(0, 0, c.width, c.height);
        ctx2.drawImage(img, 0, 0, c.width, c.height);
        ctx2.lineCap = "round";
        ctx2.lineJoin = "round";
        ctx2.lineWidth = capturedLineWidth;
        ctx2.strokeStyle = capturedIsEraser ? "#ffffff" : capturedColor;
        ctx2.fillStyle = capturedIsEraser ? "#ffffff" : capturedColor;
        ctx2.globalCompositeOperation = capturedIsEraser ? "destination-out" : "source-over";
        drawSmoothStroke(ctx2, thinPoints(rawPoints));
        ctx2.globalCompositeOperation = "source-over";
        saveSketchToActiveQuestion();
      };
      img.src = snapshot;
    } else {
      const ctx2 = canvas.getContext("2d");
      if (ctx2) ctx2.globalCompositeOperation = "source-over";
      saveSketchToActiveQuestion();
    }
  };

  const clearSketch = () => {
    pushCurrentSketchHistory();
    paintSketchBackground();
    setSketchHasDrawing(false);
    setSketchElements([]);
    setDraftSketchElement(null);
    setSelectedSketchElementId(null);
    setActiveTextEditId(null);
    setActiveTableCellEdit(null);
    skipNextSketchReloadRef.current = true;
    syncSketchQuestionData(null, []);
  };

  const clearSketchSelection = () => {
    setSelectedSketchElementId(null);
    setActiveTextEditId(null);
    setActiveTableCellEdit(null);
  };

  const undoSketch = () => {
    const previousSnapshot = sketchUndoStackRef.current.pop();
    if (!previousSnapshot) return;
    syncSketchHistoryState();
    applySketchSnapshot(previousSnapshot.snapshot || blankSketchSnapshotRef.current, previousSnapshot.elements, true);
  };

  const commitSketchElements = (nextElements: SketchElement[]) => {
    setSketchElements(nextElements);
    sketchElementsRef.current = nextElements;
    skipNextSketchReloadRef.current = true;
    syncSketchQuestionData(activeQuestion?.sketchBase64 || null, nextElements);
  };

  const focusSketchElement = (elementId: string) => {
    const exists = sketchElementsRef.current.some((element) => element.id === elementId);
    if (!exists) return;
    setSelectedSketchElementId(elementId);
  };

  const focusTableCellEditor = (elementId: string, row: number, column: number) => {
    focusSketchElement(elementId);
    setActiveTextEditId(null);
    setActiveTableCellEdit({ elementId, row, column });
  };

  const openTableCellPrompt = async (table: SketchTableElement, row: number, column: number) => {
    focusTableCellEditor(table.id, row, column);
    const currentText = getTableCellText(table, row, column);
    const nextValue = await showPrompt({
      title: `Hücre Metni • ${row + 1}. Satır ${column + 1}. Sütun`,
      message: "Hücre içine yazılacak metni girin.",
      placeholder: "Hücre metni",
      defaultValue: currentText,
      confirmText: "Kaydet",
      cancelText: "Vazgeç",
      multiline: true,
    });

    if (!nextValue.trim()) {
      return;
    }

    updateTableCellText(table.id, row, column, nextValue);
  };

  const updateSketchElement = (elementId: string, updater: (element: SketchElement) => SketchElement) => {
    const nextElements = sketchElements.map((element) => (element.id === elementId ? updater(element) : element));
    commitSketchElements(nextElements);
  };

  const updateTableCellText = (elementId: string, row: number, column: number, text: string) => {
    const cellKey = getTableCellKey(row, column);
    updateSketchElement(elementId, (current) => {
      if (current.type !== "table") {
        return current;
      }

      const nextCellTexts = cloneTableCellTexts(current.cellTexts);
      if (text.trim()) {
        nextCellTexts[cellKey] = text;
      } else {
        delete nextCellTexts[cellKey];
      }

      return {
        ...current,
        cellTexts: nextCellTexts,
      };
    });
  };

  const removeSelectedSketchElement = () => {
    if (!selectedSketchElementId) return;
    pushCurrentSketchHistory();
    const nextElements = sketchElements.filter((element) => element.id !== selectedSketchElementId);
    setSelectedSketchElementId(null);
    setActiveTextEditId(null);
    setActiveTableCellEdit(null);
    commitSketchElements(nextElements);
  };

  const startSketchElementResize = (event: React.PointerEvent<HTMLDivElement>, element: SketchElement) => {
    event.stopPropagation();
    pushCurrentSketchHistory();
    focusSketchElement(element.id);
    setActiveTextEditId(element.type === "text" ? element.id : null);
    setActiveTableCellEdit(null);
    sketchInteractionRef.current = {
      mode: "resize",
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      elementId: element.id,
      createType: null,
      originX: element.x,
      originY: element.y,
      originWidth: element.width,
      originHeight: element.height,
    };
    (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
  };

  const startSketchElementMove = (event: React.PointerEvent<HTMLDivElement>, element: SketchElement) => {
    if ((event.target as HTMLElement).closest("textarea")) return;
    if (element.type === "text" && activeTextEditId === element.id) return;
    event.stopPropagation();
    pushCurrentSketchHistory();
    focusSketchElement(element.id);
    setActiveTextEditId(null);
    setActiveTableCellEdit(null);
    sketchInteractionRef.current = {
      mode: "move",
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      elementId: element.id,
      createType: null,
      originX: element.x,
      originY: element.y,
      originWidth: element.width,
      originHeight: element.height,
    };
    (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
  };

  const handleSketchOverlayPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const interaction = sketchInteractionRef.current;
    if (interaction.pointerId !== event.pointerId || !interaction.elementId) return;
    const deltaX = event.clientX - interaction.startX;
    const deltaY = event.clientY - interaction.startY;
    setSketchElements((prev) => {
      const next = prev.map((element) =>
        element.id === interaction.elementId
          ? {
              ...element,
              ...(interaction.mode === "move"
                ? {
                    x: clamp(interaction.originX + deltaX, 0, SKETCH_CANVAS_WIDTH - element.width),
                    y: clamp(interaction.originY + deltaY, 0, SKETCH_CANVAS_HEIGHT - element.height),
                  }
                : {
                    width:
                      element.type === "square"
                        ? Math.max(
                            clamp(interaction.originWidth + deltaX, MIN_ELEMENT_SIZE, SKETCH_CANVAS_WIDTH - interaction.originX),
                            clamp(interaction.originHeight + deltaY, MIN_ELEMENT_SIZE, SKETCH_CANVAS_HEIGHT - interaction.originY)
                          )
                        : clamp(interaction.originWidth + deltaX, MIN_ELEMENT_SIZE, SKETCH_CANVAS_WIDTH - interaction.originX),
                    height:
                      element.type === "square"
                        ? Math.max(
                            clamp(interaction.originWidth + deltaX, MIN_ELEMENT_SIZE, SKETCH_CANVAS_WIDTH - interaction.originX),
                            clamp(interaction.originHeight + deltaY, MIN_ELEMENT_SIZE, SKETCH_CANVAS_HEIGHT - interaction.originY)
                          )
                        : clamp(interaction.originHeight + deltaY, MIN_ELEMENT_SIZE, SKETCH_CANVAS_HEIGHT - interaction.originY),
                  }),
            }
          : element
      );
      sketchElementsRef.current = next;
      return next;
    });
  };

  const handleSketchOverlayPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const interaction = sketchInteractionRef.current;
    if ((interaction.mode !== "resize" && interaction.mode !== "move") || interaction.pointerId !== event.pointerId) return;
    sketchInteractionRef.current.mode = null;
    sketchInteractionRef.current.pointerId = null;
    sketchInteractionRef.current.createType = null;
    (event.currentTarget as HTMLDivElement).releasePointerCapture(event.pointerId);
    commitSketchElements(sketchElementsRef.current);
  };

  const addDroppedItemToSketch = async (itemType: SketchPlacementTool, clientX: number, clientY: number) => {
    const tableDimensions = itemType === "table" ? await requestTableDimensions() : null;
    if (itemType === "table" && !tableDimensions) {
      setPendingDroppedItem(null);
      setShapePickerOpen(false);
      return;
    }

    pushCurrentSketchHistory();
    const point = getSketchPointFromClient(clientX, clientY);
    const nextElement = createSketchElement(
      itemType,
      point.x - (itemType === "text" ? DEFAULT_TEXTBOX_WIDTH / 2 : itemType === "table" ? (DEFAULT_TABLE_CELL_WIDTH * (tableDimensions?.columns || 4)) / 2 : DEFAULT_SHAPE_SIZE / 2),
      point.y - (itemType === "text" ? DEFAULT_TEXTBOX_HEIGHT / 2 : itemType === "table" ? (DEFAULT_TABLE_CELL_HEIGHT * (tableDimensions?.rows || 3)) / 2 : DEFAULT_SHAPE_SIZE / 2),
      tableDimensions || undefined
    );
    const nextElements = [...sketchElementsRef.current, nextElement];
    commitSketchElements(nextElements);
    setSelectedSketchElementId(nextElement.id);
    setPendingDroppedItem(null);
    setShapePickerOpen(false);
    if (itemType === "text") {
      setActiveTextEditId(nextElement.id);
    }
  };

  const updateState = (updater: (prev: QuestionBankState) => QuestionBankState) => {
    setState((prev) => (prev ? updater(prev) : prev));
  };

  const goToPreviewQuestion = (direction: "previous" | "next") => {
    if (!state?.sorular?.length || previewQuestionIndex < 0) return;
    const nextIndex = direction === "previous" ? previewQuestionIndex - 1 : previewQuestionIndex + 1;
    const nextQuestion = state.sorular[nextIndex];
    if (!nextQuestion) return;
    setActiveQuestionId(nextQuestion.id);
  };

  const ensureSira = (items: QuestionItem[]) => items.map((q, idx) => ({ ...q, sira: idx + 1 }));

  const clearPendingSave = () => {
    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  };

  const persistState = async (
    nextState: QuestionBankState,
    options: {
      keepalive?: boolean;
      updateIndicators?: boolean;
    } = {}
  ) => {
    const questionBankId = questionBankIdRef.current;
    if (!questionBankId) return;
    const { keepalive = false, updateIndicators = true } = options;

    if (updateIndicators && isMountedRef.current) {
      setIsSaving(true);
    }

    try {
      await ApiService.updateQuestionBank(questionBankId, buildQuestionBankUpdatePayload(nextState), keepalive ? { keepalive: true } : {});
      if (updateIndicators && isMountedRef.current) {
        setLastSavedAt(new Date().toISOString());
      }
    } finally {
      if (updateIndicators && isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  const flushPendingSave = async (
    options: {
      keepalive?: boolean;
      updateIndicators?: boolean;
    } = {}
  ) => {
    const pendingState = pendingStateRef.current;
    if (!pendingState) {
      return;
    }

    clearPendingSave();
    pendingStateRef.current = null;
    try {
      await persistState(pendingState, options);
    } catch (error) {
      pendingStateRef.current = pendingState;
      throw error;
    }
  };

  useEffect(() => {
    const flushPendingSaveForPageExit = () => {
      void flushPendingSave({ keepalive: true, updateIndicators: false }).catch((error) => {
        console.error("Question bank save failed during page exit:", error);
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingSaveForPageExit();
      }
    };

    window.addEventListener("pagehide", flushPendingSaveForPageExit);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flushPendingSaveForPageExit);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [id]);

  const handleNavigateToList = async () => {
    try {
      await flushPendingSave();
      navigate("/question-bank-indesign");
    } catch (error: any) {
      await showAlert({
        title: "Kayıt Hatası",
        message: error?.message || "Çıkmadan önce değişiklikler kaydedilemedi.",
        type: "error",
      });
    }
  };

  const updateSetupData = async () => {
    const testAdi = setupData.test_adi.trim();
    const soruSayisi = Number(setupData.soru_sayisi);

    if (!testAdi) {
      await showAlert({ title: "Uyarı", message: "Test adı zorunludur.", type: "warning" });
      return;
    }

    if (!Number.isInteger(soruSayisi) || soruSayisi < 1 || soruSayisi > 200) {
      await showAlert({ title: "Uyarı", message: "Soru sayısı 1 ile 200 arasında olmalıdır.", type: "warning" });
      return;
    }

    updateState((prev) => {
      const currentQuestions = prev.sorular || [];
      let sorular = currentQuestions;
      const defaultQuestion = currentQuestions[0];

      if (currentQuestions.length < soruSayisi) {
        sorular = [
          ...currentQuestions,
          ...Array.from({ length: soruSayisi - currentQuestions.length }, (_, index) =>
            normalizeQuestionForEditor({
              ...createEmptyQuestion(currentQuestions.length + index + 1),
              templateType: defaultQuestion?.templateType || "classic_vertical",
              choiceLayout: defaultQuestion?.choiceLayout || "vertical",
              secenek_sayisi: defaultQuestion?.secenek_sayisi || MAX_CHOICE_COUNT,
              showIntroText: Boolean(defaultQuestion?.showIntroText),
            })
          ),
        ];
      } else if (currentQuestions.length > soruSayisi) {
        sorular = currentQuestions.slice(0, soruSayisi);
      }

      const nextQuestions = ensureSira(sorular);
      if (!nextQuestions.find((question) => question.id === activeQuestionId)) {
        setActiveQuestionId(nextQuestions[0]?.id || null);
      }

      return {
        ...prev,
        test_adi: testAdi,
        kategori: setupData.kategori.trim() || undefined,
        soru_sayisi: nextQuestions.length,
        sorular: nextQuestions,
      };
    });
    setSetupOpen(false);
  };

  const updateActiveQuestion = (updater: (question: QuestionItem) => QuestionItem) => {
    if (!activeQuestionId) return;
    updateState((prev) => ({
      ...prev,
      sorular: prev.sorular.map((q) => (q.id === activeQuestionId ? updater(q) : q)),
    }));
  };

  const applyLayoutSettingsToActiveQuestion = (settings: { choiceLayout: QuestionChoiceLayout; showIntroText: boolean }) => {
    updateActiveQuestion((question) => applySharedQuestionLayout(question, settings));
  };

  const updateActiveQuestionAndPersist = (updater: (question: QuestionItem) => QuestionItem) => {
    if (!activeQuestionId || !id || !hydratedRef.current) return;

    const currentState = stateRef.current;
    if (!currentState) return;

    const nextState: QuestionBankState = {
      ...currentState,
      sorular: currentState.sorular.map((question) => (question.id === activeQuestionId ? updater(question) : question)),
    };

    stateRef.current = nextState;
    setState(nextState);
    clearPendingSave();
    void persistState(nextState).catch((error) => {
      console.error("Question bank save failed:", error);
    });
  };

  const getMathTargetEditor = (field: MathTargetField): RichContentEditorHandle | null => {
    if (field === "question") return questionEditorRef.current;
    return choiceEditorRefs.current[field];
  };

  const focusMathTarget = (field: MathTargetField) => {
    window.requestAnimationFrame(() => {
      getMathTargetEditor(field)?.focus();
    });
  };

  const insertMathSymbol = (action: MathSymbolAction, preferredField?: MathTargetField) => {
    if (!activeQuestion) return;

    const field = preferredField || openMathPicker?.field || "question";
    getMathTargetEditor(field)?.applyMathAction(action);
    setOpenMathPicker(null);
    focusMathTarget(field);
  };

  const toggleMathPicker = (field: MathTargetField) => {
    if (!activeQuestion) return;
    focusMathTarget(field);
    const isSameTarget = openMathPicker?.questionId === activeQuestion.id && openMathPicker.field === field;
    mathPickerPlacementRef.current = null;
    setMathPickerPosition(null);
    setOpenMathPicker(isSameTarget ? null : { questionId: activeQuestion.id, field });
  };

  const handleDragStart = (questionId: string) => setDraggedQuestionId(questionId);

  const handleDrop = (targetQuestionId: string) => {
    if (!draggedQuestionId || !state || draggedQuestionId === targetQuestionId) {
      setDraggedQuestionId(null);
      return;
    }

    const fromIndex = state.sorular.findIndex((q) => q.id === draggedQuestionId);
    const toIndex = state.sorular.findIndex((q) => q.id === targetQuestionId);

    if (fromIndex < 0 || toIndex < 0) {
      setDraggedQuestionId(null);
      return;
    }

    const reordered = [...state.sorular];
    const [item] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, item);

    updateState((prev) => ({
      ...prev,
      sorular: ensureSira(reordered),
      soru_sayisi: reordered.length,
    }));
    setDraggedQuestionId(null);
  };

  const addQuestion = () => {
    let createdQuestionId: string | null = null;

    updateState((prev) => {
      const defaultQuestion = prev.sorular[0];
      const nextQuestion = normalizeQuestionForEditor({
        ...createEmptyQuestion(prev.sorular.length + 1),
        templateType: defaultQuestion?.templateType || "classic_vertical",
        choiceLayout: defaultQuestion?.choiceLayout || "vertical",
        secenek_sayisi: defaultQuestion?.secenek_sayisi || MAX_CHOICE_COUNT,
        showIntroText: Boolean(defaultQuestion?.showIntroText),
      });
      createdQuestionId = nextQuestion.id;
      const next = [...prev.sorular, nextQuestion];
      return {
        ...prev,
        sorular: ensureSira(next),
        soru_sayisi: next.length,
      };
    });

    if (createdQuestionId) {
      setActiveQuestionId(createdQuestionId);
    }
  };

  const deleteQuestion = async (questionId: string) => {
    if (!state) return;

    if (state.sorular.length === 1) {
      await showAlert({ title: "Uyarı", message: "En az bir soru kalmalıdır.", type: "warning" });
      return;
    }

    const confirmed = await showConfirm({
      title: "Soruyu Sil",
      message: "Bu soruyu silmek istediğinize emin misiniz?",
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    updateState((prev) => {
      const filtered = prev.sorular.filter((q) => q.id !== questionId);
      const normalized = ensureSira(filtered);

      if (activeQuestionId === questionId) {
        setActiveQuestionId(normalized[0]?.id || null);
      }

      return {
        ...prev,
        sorular: normalized,
        soru_sayisi: normalized.length,
      };
    });
  };

  const startRecording = async () => {
    if (!activeQuestion) {
      showToast("Önce bir soru seçin.", "warning");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        if (audioChunksRef.current.length === 0) return;

        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setIsTranscribing(true);

        try {
          const result = await ApiService.transcribeAudio(audioBlob, "tr");
          if (result?.text?.trim()) {
            updateActiveQuestion((q) =>
              setQuestionContent(q, createRichContentDocument(q.soru_metni ? `${q.soru_metni} ${result.text.trim()}` : result.text.trim()))
            );
            showToast("Dikte metne eklendi.", "success");
          }
        } catch (error: any) {
          await showAlert({
            title: "Dikte Hatası",
            message: error?.message || "Ses yazıya çevrilemedi.",
            type: "error",
          });
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error: any) {
      await showAlert({
        title: "Mikrofon Hatası",
        message: "Mikrofona erişilemedi. Tarayıcı izinlerini kontrol edin.",
        type: "error",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
      return;
    }
    startRecording();
  };

  const generateImageForActiveQuestion = async () => {
    if (!activeQuestion) return;
    const prompt = imagePrompt.trim();

    if (!prompt) {
      await showAlert({ title: "Uyarı", message: "Lütfen görsel promptu girin.", type: "warning" });
      return;
    }

    setImageGenerating(true);
    try {
      const result = await ApiService.generateQuestionImage({
        prompt,
        aspectRatio: "1:1",
        questionContext: activeQuestionImageContext,
      });
      updateActiveQuestionAndPersist((q) => ({
        ...q,
        image: {
          mimeType: result.mimeType || "image/png",
          base64: result.base64,
          prompt,
          trimBottomRatio: 0,
        },
      }));
      showToast("Görsel üretildi.", "success");
      setImageModalOpen(false);
    } catch (error: any) {
      await showAlert({
        title: "Görsel Üretim Hatası",
        message: error?.message || "Gemini ile görsel üretimi başarısız oldu.",
        type: "error",
      });
    } finally {
      setImageGenerating(false);
    }
  };

  const generateAutomaticImageForActiveQuestion = async () => {
    if (!activeQuestion) return;
    const automaticQuestionContext = activeQuestionImageContext
      ? {
          ...activeQuestionImageContext,
          correctAnswer: null,
        }
      : undefined;
    const hasQuestionContext = Boolean(
      automaticQuestionContext?.questionText?.trim() ||
        automaticQuestionContext?.introText?.trim() ||
        automaticQuestionContext?.subject?.trim()
    );

    if (!hasQuestionContext) {
      await showAlert({ title: "Uyarı", message: "Otomatik görsel için önce soru metni veya kategori bilgisi girin.", type: "warning" });
      return;
    }

    const automaticPrompt = [
      "Bu soru için ders/kategori, soru metni ve şıklara göre en uygun sade destek görselini otomatik üret.",
      "Önce sorunun hangi ders ve konuya ait olduğunu çıkar; görsel yalnız bu soruyu çözmeyi desteklesin.",
      "Soru matematik, geometri, fen, grafik veya şema içeriyorsa 2D, net, teknik ve ölçüye sadık diyagram üret.",
      "Açı, eğim, oran, paralellik, diklik, yön ve konum bilgilerini soru metnindeki haliyle birebir koru.",
      "Soru kökü, şıklar veya uzun açıklama yazma; yalnız gerekli kısa sembol ve etiketleri kullan.",
      "Doğru cevabı, çözüm yolunu veya cevabı ele veren ipucunu görselde gösterme.",
      "Gereksiz dekor, arka plan, fotoğraf gerçekçiliği ve rastgele nesne ekleme.",
    ].join("\n");

    setImageGenerating(true);
    try {
      const result = await ApiService.generateQuestionImage({
        prompt: automaticPrompt,
        aspectRatio: "1:1",
        questionContext: automaticQuestionContext,
      });
      updateActiveQuestionAndPersist((q) => ({
        ...q,
        image: {
          mimeType: result.mimeType || "image/png",
          base64: result.base64,
          prompt: "automatic-question-context",
          trimBottomRatio: 0,
        },
      }));
      showToast("Görsel otomatik oluşturuldu.", "success");
      setImageModalOpen(false);
    } catch (error: any) {
      await showAlert({
        title: "Otomatik Görsel Hatası",
        message: error?.message || "Soruya göre otomatik görsel üretilemedi.",
        type: "error",
      });
    } finally {
      setImageGenerating(false);
    }
  };

  const containsMathNotation = (value: string) => /[\d=+\-*/()[\]{}<>≤≥≠≈∞π√∛^₀₁₂₃₄₅₆₇₈₉⁰¹²³⁴⁵⁶⁷⁸⁹]/.test(value);

  const buildSketchInternalPrompt = (questionText: string) => {
    const question = questionText.trim();
    const mathSensitive = containsMathNotation(question);

    if (question) {
      return [
        "Bu soru için eğitimde kullanılacak sade bir teknik görsel üret.",
        "Soru metnine bağlı kal, gereksiz dekor ekleme.",
        "Eskizde görülen biçimi koru; kendi yorumunu katma.",
        "Matematiksel ifade, sembol ve işaretleri aynen koru.",
        "Parantezleri, kök işaretlerini, eşittir çizgisini, eksi işaretini ve sayı karakterlerini değiştirme.",
        "Yuvarlak parantezi köşeli paranteze, kutuya veya çerçeveye dönüştürme.",
        "Bir ifade etrafında açıkça bir geometri şekli istenmiyorsa kutu, çerçeve veya blok çizme.",
        mathSensitive
          ? "Bu çizim matematik notasyonu içeriyor; amaç estetik yorum değil, sembolleri birebir ve okunaklı korumaktır."
          : "Çizimdeki işaretleri temizle ama anlamını değiştirme.",
        `Soru metni: ${question}`,
      ].join("\n");
    }

    return [
      "Ekteki eskizi referans alarak beyaz arka planlı, temiz, 2D, net bir eğitim diyagramı üret.",
      "Eskizdeki matematiksel sembolleri ve karakterleri aynen koru.",
      "Parantezleri kutuya veya köşeli paranteze dönüştürme.",
      "Kendi başına dekoratif şekil, çerçeve veya blok ekleme.",
    ].join("\n");
  };

  const generateImageFromSketchForActiveQuestion = async () => {
    if (!activeQuestion) return;
    const prompt = buildSketchInternalPrompt(activeQuestion.soru_metni);
    if (!sketchHasRenderableContent) {
      await showAlert({ title: "Uyarı", message: "Önce çizim alanına bir eskiz çizin.", type: "warning" });
      return;
    }

    setSketchGenerating(true);
    try {
      const sketchBase64 = exportSketchCompositeDataUrl();
      const result = await ApiService.generateQuestionImageFromSketch({
        prompt,
        sketchBase64,
        sketchMimeType: "image/png",
        aspectRatio: "1:1",
        questionContext: activeQuestionImageContext,
      });

      updateActiveQuestionAndPersist((q) => ({
        ...q,
        image: {
          mimeType: result.mimeType || "image/png",
          base64: result.base64,
          prompt,
          trimBottomRatio: 0,
        },
      }));
      showToast("Eskiz AI ile görsele dönüştürüldü.", "success");
      setImageModalOpen(false);
    } catch (error: any) {
      await showAlert({
        title: "Eskiz Üretim Hatası",
        message: error?.message || "Eskizden görsel üretimi başarısız oldu.",
        type: "error",
      });
    } finally {
      setSketchGenerating(false);
    }
  };

  const handleReferenceImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeQuestion) return;

    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;
    if (!file.type.startsWith("image/")) {
      await showAlert({ title: "Uyarı", message: "Lütfen geçerli bir görsel dosyası yükleyin.", type: "warning" });
      return;
    }

    const fileToDataUrl = () =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Dosya okunamadı."));
        reader.readAsDataURL(file);
      });

    const normalizeReferenceImage = (dataUrl: string) =>
      new Promise<{ dataUrl: string; mimeType: string }>((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          const maxDimension = 1600;
          const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
          const width = Math.max(1, Math.round(image.width * scale));
          const height = Math.max(1, Math.round(image.height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            reject(new Error("Görsel işlenemedi."));
            return;
          }

          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(image, 0, 0, width, height);
          resolve({
            dataUrl: canvas.toDataURL("image/jpeg", 0.9),
            mimeType: "image/jpeg",
          });
        };
        image.onerror = () => reject(new Error("Görsel yüklenemedi."));
        image.src = dataUrl;
      });

    try {
      const originalDataUrl = await fileToDataUrl();
      const normalized = await normalizeReferenceImage(originalDataUrl);
      const [, base64 = ""] = normalized.dataUrl.split(",");
      updateActiveQuestionAndPersist((q) => ({
        ...q,
        referenceImage: {
          mimeType: normalized.mimeType,
          base64,
          fileName: file.name,
        },
      }));
      showToast("Referans görsel yüklendi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "Yükleme Hatası",
        message: error?.message || "Görsel yüklenemedi.",
        type: "error",
      });
    }
  };

  const useReferenceImageDirectlyForActiveQuestion = async () => {
    if (!activeQuestion?.referenceImage) {
      await showAlert({ title: "Uyarı", message: "Önce bir görsel yükleyin.", type: "warning" });
      return;
    }

    updateActiveQuestionAndPersist((q) => ({
      ...q,
      image: {
        mimeType: q.referenceImage?.mimeType || "image/png",
        base64: q.referenceImage?.base64 || "",
        prompt: "uploaded-reference",
        fileName: q.referenceImage?.fileName,
        trimBottomRatio: 0,
      },
    }));
    showToast("Yüklenen görsel doğrudan eklendi.", "success");
  };

  const generateImageFromReferenceForActiveQuestion = async () => {
    if (!activeQuestion?.referenceImage) {
      await showAlert({ title: "Uyarı", message: "Önce bir referans görsel yükleyin.", type: "warning" });
      return;
    }

    const prompt = imagePrompt.trim();
    if (!prompt) {
      await showAlert({ title: "Uyarı", message: "Referans görsel için bir prompt girin.", type: "warning" });
      return;
    }

    setImageGenerating(true);
    try {
      const result = await ApiService.generateQuestionImageFromReference({
        prompt,
        referenceBase64: activeQuestion.referenceImage.base64,
        referenceMimeType: activeQuestion.referenceImage.mimeType,
        aspectRatio: "1:1",
        questionContext: activeQuestionImageContext,
      });
      updateActiveQuestionAndPersist((q) => ({
        ...q,
        image: {
          mimeType: result.mimeType || "image/png",
          base64: result.base64,
          prompt,
          trimBottomRatio: 0,
        },
      }));
      showToast("Referans görsele göre yeni görsel üretildi.", "success");
      setImageModalOpen(false);
    } catch (error: any) {
      await showAlert({
        title: "Referans Görsel Hatası",
        message: error?.message || "Referans görsel ile üretim başarısız oldu.",
        type: "error",
      });
    } finally {
      setImageGenerating(false);
    }
  };

  const useSketchAsImageForActiveQuestion = async () => {
    if (!activeQuestion) return;

    if (!sketchHasRenderableContent) {
      await showAlert({ title: "Uyarı", message: "Önce çizim alanına bir eskiz çizin.", type: "warning" });
      return;
    }

    const dataUrl = exportSketchCompositeDataUrl();
    const [, base64 = ""] = dataUrl.split(",");

    updateActiveQuestionAndPersist((q) => ({
      ...q,
      image: {
        mimeType: "image/png",
        base64,
        prompt: "raw-sketch",
        trimBottomRatio: 0,
      },
    }));
    showToast("Eskiz doğrudan görsel olarak eklendi.", "success");
  };

  const correctActiveQuestionText = async () => {
    if (!activeQuestion) return;
    const rawText = activeQuestion.soru_metni.trim();

    if (!rawText) {
      await showAlert({
        title: "Uyarı",
        message: "Düzeltme için önce soru metni girin.",
        type: "warning",
      });
      return;
    }

    setIsCorrectingText(true);
    try {
      const result = await ApiService.correctQuestionText({ text: rawText });
      if (result?.text?.trim()) {
        updateActiveQuestion((q) => setQuestionContent(q, createRichContentDocument(result.text.trim())));
        showToast("Türkçe imla düzeltildi.", "success");
      }
    } catch (error: any) {
      await showAlert({
        title: "İmla Düzeltme Hatası",
        message: error?.message || "Metin düzeltilemedi.",
        type: "error",
      });
    } finally {
      setIsCorrectingText(false);
    }
  };

  const normalizeInlineImageFile = (file: File) =>
    new Promise<QuestionImage>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const image = new Image();
        image.onload = () => {
          const maxDimension = 1400;
          const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
          const width = Math.max(1, Math.round(image.width * scale));
          const height = Math.max(1, Math.round(image.height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            reject(new Error("Görsel işlenemedi."));
            return;
          }

          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(image, 0, 0, width, height);

          const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
          const [, base64 = ""] = dataUrl.split(",");

          resolve({
            mimeType: "image/jpeg",
            base64,
            prompt: file.name,
            fileName: file.name,
            trimBottomRatio: 0,
          });
        };
        image.onerror = () => reject(new Error("Görsel yüklenemedi."));
        image.src = String(reader.result || "");
      };
      reader.onerror = () => reject(new Error("Dosya okunamadı."));
      reader.readAsDataURL(file);
    });

  const appendGeneratedImageToChoice = (choice: ChoiceKey, image: QuestionImage) => {
    updateActiveQuestion((question) => {
      const currentDocument = getChoiceContent(question, choice);
      return setChoiceContent(question, choice, {
        ...currentDocument,
        blocks: [
          ...currentDocument.blocks,
          {
            id: crypto.randomUUID(),
            type: "image",
            image,
          },
        ],
      });
    });
  };

  const triggerChoiceImageUpload = (choice: ChoiceKey) => {
    setRichImageUploadTarget(choice);
    richImageInputRef.current?.click();
  };

  const handleRichChoiceImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const targetChoice = richImageUploadTarget;
    event.target.value = "";
    setRichImageUploadTarget(null);

    if (!file || !targetChoice) return;
    if (!file.type.startsWith("image/")) {
      await showAlert({ title: "Uyarı", message: "Lütfen geçerli bir görsel dosyası yükleyin.", type: "warning" });
      return;
    }

    try {
      const image = await normalizeInlineImageFile(file);
      appendGeneratedImageToChoice(targetChoice, image);
      showToast(`${targetChoice} şıkkına görsel eklendi.`, "success");
    } catch (error: any) {
      await showAlert({
        title: "Yükleme Hatası",
        message: error?.message || "Görsel eklenemedi.",
        type: "error",
      });
    }
  };

  const openChoiceImageModal = (choice: ChoiceKey) => {
    if (!activeQuestion) return;
    setChoiceImageTarget(choice);
    setChoiceImageInputMode("text");
    setChoiceImagePrompt(extractPlainTextFromRichContent(getChoiceContent(activeQuestion, choice)));
    setChoiceReferenceImage(null);
    setChoiceSketchHasDrawing(false);
    choiceSketchUndoStackRef.current = [];
    setChoiceSketchUndoSize(0);
    setChoiceImageModalOpen(true);
  };

  const closeChoiceImageModal = (force = false) => {
    if (!force && (choiceImageGenerating || choiceSketchGenerating)) return;
    setChoiceImageModalOpen(false);
    setChoiceImageTarget(null);
    setChoiceImagePrompt("");
    setChoiceImageInputMode("text");
    setChoiceReferenceImage(null);
    setChoiceSketchHasDrawing(false);
    choiceSketchUndoStackRef.current = [];
    setChoiceSketchUndoSize(0);
  };

  const buildChoiceImagePromptWithLanguageLock = (rawPrompt: string) =>
    [
      "Use the user prompt exactly in its original language.",
      "Do not translate or rewrite the language-specific wording.",
      "If text appears in the image, keep the same language as the user prompt.",
      "",
      `User prompt: ${rawPrompt}`,
    ].join("\n");

  const submitChoiceImageTextGeneration = async () => {
    const choice = choiceImageTarget;
    const normalizedPrompt = choiceImagePrompt.trim();
    if (!choice) return;
    if (!normalizedPrompt) {
      await showAlert({ title: "Uyarı", message: "Görsel üretim promptu boş olamaz.", type: "warning" });
      return;
    }

    setChoiceImageGenerating(true);
    try {
      const languageLockedPrompt = buildChoiceImagePromptWithLanguageLock(normalizedPrompt);
      const result = await ApiService.generateQuestionImage({
        prompt: languageLockedPrompt,
        aspectRatio: "1:1",
      });
      appendGeneratedImageToChoice(choice, {
        mimeType: result.mimeType || "image/png",
        base64: result.base64,
        prompt: normalizedPrompt,
        trimBottomRatio: 0,
      });
      showToast(`${choice} şıkkı için görsel üretildi.`, "success");
      closeChoiceImageModal(true);
    } catch (error: any) {
      await showAlert({
        title: "Görsel Üretim Hatası",
        message: error?.message || "Şık görseli üretilemedi.",
        type: "error",
      });
    } finally {
      setChoiceImageGenerating(false);
    }
  };

  const getChoiceSketchCanvasCoordinates = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = choiceSketchCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const drawChoiceSketchSegment = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const canvas = choiceSketchCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.save();
    ctx.globalCompositeOperation = choiceSketchIsEraser ? "destination-out" : "source-over";
    ctx.strokeStyle = choiceSketchColor;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = choiceSketchLineWidth;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.restore();
  };

  const pushChoiceSketchUndoSnapshot = () => {
    const canvas = choiceSketchCanvasRef.current;
    if (!canvas) return;
    const snapshot = canvas.toDataURL("image/png");
    choiceSketchUndoStackRef.current.push(snapshot);
    if (choiceSketchUndoStackRef.current.length > 40) {
      choiceSketchUndoStackRef.current.shift();
    }
    setChoiceSketchUndoSize(choiceSketchUndoStackRef.current.length);
  };

  const restoreChoiceSketchFromDataUrl = (dataUrl: string) =>
    new Promise<void>((resolve, reject) => {
      const canvas = choiceSketchCanvasRef.current;
      if (!canvas) {
        resolve();
        return;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve();
        return;
      }
      const image = new Image();
      image.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve();
      };
      image.onerror = () => reject(new Error("Çizim geri yüklenemedi."));
      image.src = dataUrl;
    });

  const undoChoiceSketch = async () => {
    const snapshot = choiceSketchUndoStackRef.current.pop();
    setChoiceSketchUndoSize(choiceSketchUndoStackRef.current.length);
    if (!snapshot) return;
    try {
      await restoreChoiceSketchFromDataUrl(snapshot);
      const blank = choiceBlankSketchSnapshotRef.current;
      setChoiceSketchHasDrawing(!blank || snapshot !== blank);
    } catch (error: any) {
      await showAlert({
        title: "Geri Al Hatası",
        message: error?.message || "Son çizim adımı geri alınamadı.",
        type: "error",
      });
    }
  };

  const handleChoiceSketchPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (choiceImageInputMode !== "draw") return;
    const point = getChoiceSketchCanvasCoordinates(event);
    if (!point) return;
    event.preventDefault();
    const canvas = choiceSketchCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.setPointerCapture(event.pointerId);
    pushChoiceSketchUndoSnapshot();
    choiceStrokeStartSnapshotRef.current = canvas.toDataURL("image/png");
    choiceSketchRawPointsRef.current = [point];

    ctx.globalCompositeOperation = choiceSketchIsEraser ? "destination-out" : "source-over";
    ctx.strokeStyle = choiceSketchColor;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = choiceSketchLineWidth;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineTo(point.x + 0.1, point.y + 0.1);
    ctx.stroke();

    choiceSketchLastPointRef.current = point;
    setChoiceIsSketchDrawing(true);
    setChoiceSketchHasDrawing(true);
  };

  const handleChoiceSketchPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!choiceIsSketchDrawing) return;
    const point = getChoiceSketchCanvasCoordinates(event);
    const prev = choiceSketchLastPointRef.current;
    if (!point || !prev) return;
    event.preventDefault();
    const canvas = choiceSketchCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    choiceSketchRawPointsRef.current.push(point);
    const midX = (prev.x + point.x) / 2;
    const midY = (prev.y + point.y) / 2;
    ctx.quadraticCurveTo(prev.x, prev.y, midX, midY);
    ctx.stroke();
    choiceSketchLastPointRef.current = point;
  };

  const handleChoiceSketchPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!choiceIsSketchDrawing) return;
    const canvas = choiceSketchCanvasRef.current;
    if (canvas?.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
    const rawPoints = choiceSketchRawPointsRef.current;
    choiceSketchRawPointsRef.current = [];
    choiceSketchLastPointRef.current = null;
    setChoiceIsSketchDrawing(false);

    const snapshot = choiceStrokeStartSnapshotRef.current;
    const capturedLineWidth = choiceSketchLineWidth;
    const capturedColor = choiceSketchColor;
    const capturedIsEraser = choiceSketchIsEraser;

    if (canvas && snapshot && rawPoints.length > 1) {
      const img = new Image();
      img.onload = () => {
        const ctx2 = canvas.getContext("2d");
        if (!ctx2) return;
        ctx2.clearRect(0, 0, canvas.width, canvas.height);
        ctx2.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx2.lineCap = "round";
        ctx2.lineJoin = "round";
        ctx2.lineWidth = capturedLineWidth;
        ctx2.strokeStyle = capturedIsEraser ? "#ffffff" : capturedColor;
        ctx2.fillStyle = capturedIsEraser ? "#ffffff" : capturedColor;
        ctx2.globalCompositeOperation = capturedIsEraser ? "destination-out" : "source-over";
        drawSmoothStroke(ctx2, thinPoints(rawPoints));
        ctx2.globalCompositeOperation = "source-over";
      };
      img.src = snapshot;
    } else {
      const ctx2 = canvas?.getContext("2d");
      if (ctx2) ctx2.globalCompositeOperation = "source-over";
    }
  };

  const clearChoiceSketch = () => {
    const canvas = choiceSketchCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (choiceSketchHasDrawing) {
      pushChoiceSketchUndoSnapshot();
    }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    choiceSketchLastPointRef.current = null;
    setChoiceSketchHasDrawing(false);
  };

  const exportChoiceSketchDataUrl = () => {
    const canvas = choiceSketchCanvasRef.current;
    if (!canvas) return "";
    return canvas.toDataURL("image/png");
  };

  const useChoiceSketchAsImage = async () => {
    const choice = choiceImageTarget;
    if (!choice) return;
    if (!choiceSketchHasDrawing) {
      await showAlert({ title: "Uyarı", message: "Önce canvas alanına bir çizim yapın.", type: "warning" });
      return;
    }
    const dataUrl = exportChoiceSketchDataUrl();
    const [, base64 = ""] = dataUrl.split(",");
    appendGeneratedImageToChoice(choice, {
      mimeType: "image/png",
      base64,
      prompt: "raw-choice-sketch",
      trimBottomRatio: 0,
    });
    showToast(`${choice} şıkkına çizim eklendi.`, "success");
    closeChoiceImageModal();
  };

  const buildChoiceSketchPrompt = (choice: ChoiceKey, rawPrompt: string) => {
    const userPrompt = rawPrompt.trim();
    const choiceText = activeQuestion ? extractPlainTextFromRichContent(getChoiceContent(activeQuestion, choice)).trim() : "";
    const mathSensitive = containsMathNotation(`${choiceText} ${userPrompt}`.trim());

    if (choiceText || userPrompt) {
      return [
        "Bu şık için eğitimde kullanılacak sade bir teknik görsel üret.",
        "Şık metnine ve eskize bağlı kal, gereksiz dekor ekleme.",
        "Eskizde görülen biçimi koru; kendi yorumunu katma.",
        "Matematiksel ifade, sembol ve işaretleri aynen koru.",
        "Parantezleri, kök işaretlerini, eşittir çizgisini, eksi işaretini ve sayı karakterlerini değiştirme.",
        "Yuvarlak parantezi köşeli paranteze, kutuya veya çerçeveye dönüştürme.",
        "Bir ifade etrafında açıkça bir geometri şekli istenmiyorsa kutu, çerçeve veya blok çizme.",
        mathSensitive
          ? "Bu çizim matematik notasyonu içeriyor; amaç estetik yorum değil, sembolleri birebir ve okunaklı korumaktır."
          : "Çizimdeki işaretleri temizle ama anlamını değiştirme.",
        choiceText ? `Şık metni (${choice}): ${choiceText}` : "",
        userPrompt ? `Ek kullanıcı notu: ${userPrompt}` : "",
      ]
        .filter(Boolean)
        .join("\n");
    }

    return [
      "Ekteki eskizi referans alarak beyaz arka planlı, temiz, 2D, net bir eğitim diyagramı üret.",
      "Eskizdeki matematiksel sembolleri ve karakterleri aynen koru.",
      "Parantezleri kutuya veya köşeli paranteze dönüştürme.",
      "Kendi başına dekoratif şekil, çerçeve veya blok ekleme.",
    ].join("\n");
  };

  const generateChoiceImageFromSketch = async () => {
    const choice = choiceImageTarget;
    if (!choice) return;
    if (!choiceSketchHasDrawing) {
      await showAlert({ title: "Uyarı", message: "Önce canvas alanına bir çizim yapın.", type: "warning" });
      return;
    }
    const prompt = buildChoiceSketchPrompt(choice, choiceImagePrompt);

    setChoiceSketchGenerating(true);
    try {
      const dataUrl = exportChoiceSketchDataUrl();
      const [, sketchBase64 = ""] = dataUrl.split(",");
      const result = await ApiService.generateQuestionImageFromSketch({
        prompt,
        sketchBase64,
        sketchMimeType: "image/png",
        aspectRatio: "1:1",
      });
      appendGeneratedImageToChoice(choice, {
        mimeType: result.mimeType || "image/png",
        base64: result.base64,
        prompt: choiceImagePrompt.trim() || `choice-${choice}-sketch`,
        trimBottomRatio: 0,
      });
      showToast(`${choice} şıkkı için çizimden görsel üretildi.`, "success");
      closeChoiceImageModal(true);
    } catch (error: any) {
      await showAlert({
        title: "Görsel Üretim Hatası",
        message: error?.message || "Şık çiziminden görsel üretilemedi.",
        type: "error",
      });
    } finally {
      setChoiceSketchGenerating(false);
    }
  };

  const handleChoiceReferenceImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      await showAlert({ title: "Uyarı", message: "Lütfen geçerli bir görsel dosyası yükleyin.", type: "warning" });
      return;
    }
    try {
      const image = await normalizeInlineImageFile(file);
      setChoiceReferenceImage({
        mimeType: image.mimeType,
        base64: image.base64,
        fileName: image.fileName,
      });
      showToast("Referans görsel yüklendi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "Yükleme Hatası",
        message: error?.message || "Referans görsel yüklenemedi.",
        type: "error",
      });
    }
  };

  const useChoiceReferenceImageDirectly = async () => {
    const choice = choiceImageTarget;
    if (!choice || !choiceReferenceImage) {
      await showAlert({ title: "Uyarı", message: "Önce bir referans görsel yükleyin.", type: "warning" });
      return;
    }
    appendGeneratedImageToChoice(choice, {
      mimeType: choiceReferenceImage.mimeType,
      base64: choiceReferenceImage.base64,
      prompt: "uploaded-reference",
      fileName: choiceReferenceImage.fileName,
      trimBottomRatio: 0,
    });
    showToast(`${choice} şıkkına yüklenen görsel eklendi.`, "success");
    closeChoiceImageModal();
  };

  const generateChoiceImageFromReference = async () => {
    const choice = choiceImageTarget;
    if (!choice || !choiceReferenceImage) {
      await showAlert({ title: "Uyarı", message: "Önce bir referans görsel yükleyin.", type: "warning" });
      return;
    }
    const normalizedPrompt = choiceImagePrompt.trim();
    if (!normalizedPrompt) {
      await showAlert({ title: "Uyarı", message: "Referans ile üretim için prompt girin.", type: "warning" });
      return;
    }

    setChoiceImageGenerating(true);
    try {
      const result = await ApiService.generateQuestionImageFromReference({
        prompt: buildChoiceImagePromptWithLanguageLock(normalizedPrompt),
        referenceBase64: choiceReferenceImage.base64,
        referenceMimeType: choiceReferenceImage.mimeType,
        aspectRatio: "1:1",
      });
      appendGeneratedImageToChoice(choice, {
        mimeType: result.mimeType || "image/png",
        base64: result.base64,
        prompt: normalizedPrompt,
        trimBottomRatio: 0,
      });
      showToast(`${choice} şıkkı için referans görselden üretim tamamlandı.`, "success");
      closeChoiceImageModal(true);
    } catch (error: any) {
      await showAlert({
        title: "Referans Görsel Hatası",
        message: error?.message || "Referans görsel ile şık üretimi başarısız oldu.",
        type: "error",
      });
    } finally {
      setChoiceImageGenerating(false);
    }
  };

  const validateBeforeExport = (targetState: QuestionBankState | null) => {
    if (!targetState) return ["Soru seti oluşturulmadı."];

    const errors: string[] = [];
    targetState.sorular.forEach((question) => {
      const visibleChoiceKeys = getVisibleChoiceKeys(question.secenek_sayisi);

      if (!hasRenderableRichContent(question.questionContent)) {
        errors.push(`Soru ${question.sira}: Soru metni boş olamaz.`);
      }

      CHOICE_KEYS.forEach((key) => {
        if (!visibleChoiceKeys.includes(key)) {
          return;
        }

        if (!hasRenderableRichContent(question.choiceContents?.[key] || null)) {
          errors.push(`Soru ${question.sira}: ${key} şıkkı boş olamaz.`);
        }
      });

      if (!question.dogru_cevap || !visibleChoiceKeys.includes(question.dogru_cevap)) {
        errors.push(`Soru ${question.sira}: Doğru cevap seçilmelidir.`);
      }
    });

    return errors;
  };

  const exportZip = async () => {
    const currentState = pendingStateRef.current || stateRef.current;
    if (!currentState || !id) return;

    const errors = validateBeforeExport(currentState);
    if (errors.length > 0) {
      await showAlert({
        title: "Eksik Alanlar",
        message: errors.slice(0, 15).join("\n"),
        type: "warning",
      });
      return;
    }

    setExporting(true);
    try {
      await flushPendingSave();
      const { blob, fileName } = await ApiService.downloadQuestionBank(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || `${sanitizeFileName(currentState.test_adi)}_indesign.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("ZIP dosyası indirildi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "Export Hatası",
        message: error?.message || "ZIP oluşturulurken hata oluştu.",
        type: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  const renderedMathPicker =
    openMathPicker && mathPickerPosition
      ? createPortal(
          <div
            ref={mathPickerSurfaceRef}
            className="fixed z-[240]"
            style={{
              top: mathPickerPosition.top,
              left: mathPickerPosition.left,
            }}>
            <MathSymbolPicker
              onSelect={(item) => insertMathSymbol(item, openMathPicker.field)}
              maxHeight={mathPickerPosition.maxHeight}
            />
          </div>,
          document.body
        )
      : null;

  const renderedImageModal =
    activeQuestion ? (
      <Modal isOpen={imageModalOpen} onClose={() => setImageModalOpen(false)} title="Görsel Ekle" size="xl">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition ${imageInputMode === "text" ? "border-indigo-400 bg-white shadow-sm" : "border-indigo-200 bg-white/70 hover:bg-white"}`}>
              <input
                type="radio"
                name="image_input_mode_modal"
                checked={imageInputMode === "text"}
                onChange={() => setImageInputMode("text")}
                className="mt-1"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm font-medium text-gray-900">Yazı ile oluştur</div>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setImageInputMode("text");
                      void generateAutomaticImageForActiveQuestion();
                    }}
                    disabled={imageGenerating}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                    {imageGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    Otomatik Oluştur
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">Görmek istediğiniz resmi tarif edin.</div>
              </div>
            </label>

            <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition ${imageInputMode === "draw" ? "border-indigo-400 bg-white shadow-sm" : "border-indigo-200 bg-white/70 hover:bg-white"}`}>
              <input
                type="radio"
                name="image_input_mode_modal"
                checked={imageInputMode === "draw"}
                onChange={() => setImageInputMode("draw")}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">Çizim ile oluştur</div>
                <div className="text-xs text-gray-500 mt-1">Basit bir çizim yapın, sistem bunu düzenlesin.</div>
              </div>
            </label>

            <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition ${imageInputMode === "upload" ? "border-indigo-400 bg-white shadow-sm" : "border-indigo-200 bg-white/70 hover:bg-white"}`}>
              <input
                type="radio"
                name="image_input_mode_modal"
                checked={imageInputMode === "upload"}
                onChange={() => setImageInputMode("upload")}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">Fotoğraf yükle</div>
                <div className="text-xs text-gray-500 mt-1">Doğrudan kullanın veya referans alarak yeni görsel üretin.</div>
              </div>
            </label>
          </div>

          {imageInputMode === "text" ? (
            <div className="rounded-lg border border-indigo-200 bg-white p-4">
              <label className="text-sm font-medium text-gray-700 block mb-2">Resimde ne olsun?</label>
              <div className="flex flex-col md:flex-row gap-2">
                <input
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  placeholder="Örnek: açıları gösterilen sade bir geometri şekli"
                  className="flex-1 border border-indigo-200 rounded-lg px-3 py-2 text-sm bg-white"
                />
                <button
                  onClick={generateImageForActiveQuestion}
                  disabled={imageGenerating}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 text-sm">
                  {imageGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  Görsel Oluştur
                </button>
              </div>
            </div>
          ) : imageInputMode === "draw" ? (
            <div className="rounded-lg border border-indigo-200 bg-white p-4">
              {containsMathNotation(activeQuestion.soru_metni) && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  Matematik notasyonu içeren çizimlerde AI parantez, kök ve sembolleri değiştirebilir. Bu tip sorularda en doğru sonuç için "Eskizi Aynen Kullan" seçeneğini kullanın.
                </div>
              )}

              <div ref={sketchControlsRef} className="flex flex-wrap items-center gap-3 mb-3">
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSketchTool("pen");
                      setIsEraser(false);
                    }}
                    className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs border ${
                      sketchTool === "pen" ? "border-indigo-600 bg-indigo-600 text-white" : "border-gray-300 bg-white text-gray-700"
                    }`}>
                    Kalem
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShapePickerOpen((prev) => !prev)}
                      className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs border ${
                        shapePickerOpen ? "border-indigo-600 bg-indigo-600 text-white" : "border-gray-300 bg-white text-gray-700"
                      }`}>
                      Şekiller
                    </button>
                    {shapePickerOpen ? (
                      <div className="absolute left-0 top-[calc(100%+8px)] z-20 w-48 rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
                        <div className="mb-2 px-2 text-[11px] font-medium text-gray-500">Canvas içine sürükleyin</div>
                        <div className="space-y-2">
                          {SKETCH_LIBRARY_ITEMS.map((item) => (
                            <div
                              key={item.type}
                              draggable
                              onDragStart={(event) => {
                                const preview = createSketchDragPreview(item.type);
                                document.body.appendChild(preview);
                                event.dataTransfer.setData("application/x-sketch-item", item.type);
                                event.dataTransfer.effectAllowed = "copy";
                                event.dataTransfer.setDragImage(preview, item.type === "text" ? 90 : 48, item.type === "text" ? 32 : 48);
                                window.setTimeout(() => {
                                  if (preview.parentNode) {
                                    preview.parentNode.removeChild(preview);
                                  }
                                }, 0);
                                setPendingDroppedItem(item.type);
                              }}
                              onDragEnd={() => setPendingDroppedItem(null)}
                              className="flex cursor-grab items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 hover:border-indigo-300 hover:bg-indigo-50"
                            >
                              <span>{item.label}</span>
                              <span className="text-gray-400">Sürükle</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                {selectedSketchElement ? (
                  <div className="flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50/70 px-3 py-2">
                    <div className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-indigo-700 border border-indigo-200">
                      {selectedSketchElementLabel}
                    </div>
                    <label className="text-xs text-gray-700 inline-flex items-center gap-2">
                      Renk
                      <input
                        type="color"
                        value={selectedSketchElement.type === "text" ? selectedSketchElement.textColor : selectedSketchElement.strokeColor}
                        onChange={(e) => {
                          const nextColor = e.target.value;
                          setSketchColor(nextColor);
                          updateSketchElement(selectedSketchElement.id, (current) =>
                            current.type === "text" ? { ...current, textColor: nextColor } : { ...current, strokeColor: nextColor }
                          );
                        }}
                        className="w-8 h-8 p-0 border border-gray-300 rounded"
                      />
                    </label>
                    {selectedSketchElement.type !== "text" ? (
                      <label className="text-xs text-gray-700 inline-flex items-center gap-2">
                        Dolgu
                        <input
                          type="color"
                          value={selectedSketchElement.fillColor || sketchFillColor}
                          onChange={(e) => {
                            const nextFill = e.target.value;
                            setSketchFillColor(nextFill);
                            updateSketchElement(selectedSketchElement.id, (current) =>
                              current.type === "text" ? current : { ...current, fillColor: nextFill }
                            );
                          }}
                          className="w-8 h-8 p-0 border border-gray-300 rounded"
                        />
                      </label>
                    ) : null}
                    {selectedSketchElement.type === "table" ? (
                      <>
                        <label className="text-xs text-gray-700 inline-flex items-center gap-2">
                          Satır
                          <input
                            type="number"
                            min={MIN_TABLE_SIZE}
                            max={MAX_TABLE_SIZE}
                            value={selectedSketchElement.rows}
                            onChange={(e) => {
                              const nextRows = clampTableSize(Number(e.target.value) || selectedSketchElement.rows);
                              updateSketchElement(selectedSketchElement.id, (current) =>
                                current.type === "table"
                                  ? {
                                      ...current,
                                      rows: nextRows,
                                      cellTexts: normalizeTableCellTexts(current.cellTexts, nextRows, current.columns),
                                    }
                                  : current
                              );
                            }}
                            className="w-16 rounded border border-gray-300 px-2 py-1"
                          />
                        </label>
                        <label className="text-xs text-gray-700 inline-flex items-center gap-2">
                          Sütun
                          <input
                            type="number"
                            min={MIN_TABLE_SIZE}
                            max={MAX_TABLE_SIZE}
                            value={selectedSketchElement.columns}
                            onChange={(e) => {
                              const nextColumns = clampTableSize(Number(e.target.value) || selectedSketchElement.columns);
                              updateSketchElement(selectedSketchElement.id, (current) =>
                                current.type === "table"
                                  ? {
                                      ...current,
                                      columns: nextColumns,
                                      cellTexts: normalizeTableCellTexts(current.cellTexts, current.rows, nextColumns),
                                    }
                                  : current
                              );
                            }}
                            className="w-16 rounded border border-gray-300 px-2 py-1"
                          />
                        </label>
                        {activeTableCellEdit?.elementId === selectedSketchElement.id ? (
                          <div className="rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-[11px] font-medium text-indigo-700">
                            {formatTableCellCoordinate(
                              selectedSketchElement,
                              activeTableCellEdit.row,
                              activeTableCellEdit.column
                            )}
                          </div>
                        ) : null}
                      </>
                    ) : null}
                    <label className="text-xs text-gray-700 inline-flex items-center gap-2">
                      Kalınlık
                      <input
                        type="range"
                        min={1}
                        max={12}
                        value={selectedSketchElement.strokeWidth}
                        onChange={(e) => {
                          const nextWidth = Number(e.target.value);
                          setSketchLineWidth(nextWidth);
                          updateSketchElement(selectedSketchElement.id, (current) => ({ ...current, strokeWidth: nextWidth }));
                        }}
                      />
                      <span>{selectedSketchElement.strokeWidth}px</span>
                    </label>
                    {selectedSketchElement.type === "text" ? (
                      <label className="text-xs text-gray-700 inline-flex items-center gap-2">
                        Yazı
                        <input
                          type="range"
                          min={12}
                          max={36}
                          value={selectedSketchElement.fontSize}
                          onChange={(e) => {
                            const nextSize = Number(e.target.value);
                            setSketchTextSize(nextSize);
                            updateSketchElement(selectedSketchElement.id, (current) =>
                              current.type === "text" ? { ...current, fontSize: nextSize } : current
                            );
                          }}
                        />
                        <span>{selectedSketchElement.fontSize}px</span>
                      </label>
                    ) : null}
                  </div>
                ) : sketchTool === "pen" ? (
                  <>
                    <label className="text-xs text-gray-700 inline-flex items-center gap-2">
                      Renk
                      <input
                        type="color"
                        value={sketchColor}
                        disabled={isEraser}
                        onChange={(e) => setSketchColor(e.target.value)}
                        className="w-8 h-8 p-0 border border-gray-300 rounded"
                      />
                    </label>
                    <label className="text-xs text-gray-700 inline-flex items-center gap-2">
                      Kalınlık
                      <input
                        type="range"
                        min={1}
                        max={12}
                        value={sketchLineWidth}
                        onChange={(e) => setSketchLineWidth(Number(e.target.value))}
                      />
                      <span>{sketchLineWidth}px</span>
                    </label>
                  </>
                ) : null}
                {sketchTool === "pen" ? (
                  <button
                    onClick={() => {
                      setSketchTool("pen");
                      setIsEraser((prev) => !prev);
                    }}
                    className={`px-2 py-1 text-xs rounded border ${isEraser ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-700 border-gray-300"}`}>
                    {isEraser ? "Kalem" : "Silgi"}
                  </button>
                ) : null}
                {selectedSketchElement ? (
                  <button
                    onClick={removeSelectedSketchElement}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-rose-200 bg-rose-50 text-rose-700">
                    <Trash2 className="w-3.5 h-3.5" />
                    Nesneyi Sil
                  </button>
                ) : null}
                <button
                  onClick={undoSketch}
                  disabled={sketchHistorySize === 0}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 bg-white disabled:opacity-50">
                  <Undo2 className="w-3.5 h-3.5" />
                  Geri Al
                </button>
                <button
                  onClick={clearSketch}
                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 bg-white">
                  Temizle
                </button>
              </div>

              <div
                ref={sketchBoardRef}
                className="relative w-full h-[260px] border border-dashed border-indigo-200 rounded bg-white overflow-hidden"
                onDragOver={(event) => {
                  if (pendingDroppedItem || event.dataTransfer.types.includes("application/x-sketch-item")) {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "copy";
                  }
                }}
                onDrop={(event) => {
                  const itemType = event.dataTransfer.getData("application/x-sketch-item") as SketchPlacementTool;
                  if (!itemType) return;
                  event.preventDefault();
                  const { clientX, clientY } = event;
                  void addDroppedItemToSketch(itemType, clientX, clientY);
                }}
              >
                <canvas
                  ref={sketchCanvasRef}
                  width={SKETCH_CANVAS_WIDTH}
                  height={SKETCH_CANVAS_HEIGHT}
                  onPointerDown={handleSketchPointerDown}
                  onPointerMove={handleSketchPointerMove}
                  onPointerUp={handleSketchPointerUp}
                  onPointerLeave={handleSketchPointerUp}
                  className="absolute inset-0 w-full h-full touch-none"
                />

                <div className="pointer-events-none absolute inset-0 z-[2]">
                  {sketchElements.map((element) => {
                    const isSelected = selectedSketchElementId === element.id;
                    const commonStyle: React.CSSProperties = {
                      position: "absolute",
                      left: `${(element.x / SKETCH_CANVAS_WIDTH) * 100}%`,
                      top: `${(element.y / SKETCH_CANVAS_HEIGHT) * 100}%`,
                      width: `${(element.width / SKETCH_CANVAS_WIDTH) * 100}%`,
                      height: `${(element.height / SKETCH_CANVAS_HEIGHT) * 100}%`,
                    };

                    return (
                      <div
                        key={element.id}
                        style={commonStyle}
                        onPointerDown={(event) => startSketchElementMove(event, element)}
                        onDoubleClick={(event) => {
                          if (element.type !== "table") {
                            return;
                          }
                          event.stopPropagation();
                          const containerRect = event.currentTarget.getBoundingClientRect();
                          const { row, column } = getTableCellFromPointerEvent(element, containerRect, event.clientX, event.clientY);
                          void openTableCellPrompt(element, row, column);
                        }}
                        onPointerMove={handleSketchOverlayPointerMove}
                        onPointerUp={handleSketchOverlayPointerUp}
                        className={`pointer-events-auto absolute cursor-move transition-[box-shadow,border-color] ${
                          isSelected ? "ring-2 ring-indigo-500 ring-offset-1 shadow-[0_0_0_1px_rgba(99,102,241,0.22),0_10px_24px_rgba(99,102,241,0.14)]" : ""
                        }`}
                      >
                        {element.type === "text" && (activeTextEditId === element.id || isSelected) ? (
                          <textarea
                            autoFocus
                            value={element.text}
                            onPointerDown={(event) => event.stopPropagation()}
                            onDoubleClick={(event) => event.stopPropagation()}
                            onClick={(event) => event.stopPropagation()}
                            onFocus={() => {
                              setSelectedSketchElementId(element.id);
                              setActiveTextEditId(element.id);
                            }}
                            onChange={(e) =>
                              updateSketchElement(element.id, (current) =>
                                current.type === "text" ? { ...current, text: e.target.value } : current
                              )
                            }
                            onBlur={() => setActiveTextEditId(null)}
                            placeholder="Metni buraya yazın"
                            className={`h-full w-full resize-none rounded border px-2 py-1 outline-none ${
                              isSelected ? "border-indigo-400 bg-white" : "border-slate-300 bg-white/95"
                            }`}
                            style={{ color: element.textColor, fontSize: `${element.fontSize}px`, lineHeight: 1.35 }}
                          />
                        ) : element.type === "text" ? (
                          <div
                            onDoubleClick={(event) => {
                              event.stopPropagation();
                              focusSketchElement(element.id);
                              setActiveTextEditId(element.id);
                            }}
                            className={`h-full w-full rounded border px-2 py-1 whitespace-pre-wrap break-words cursor-text ${
                              isSelected ? "border-indigo-400 bg-white" : "border-slate-300 bg-white/85"
                            }`}
                            style={{ color: element.textColor, fontSize: `${element.fontSize}px`, lineHeight: 1.35 }}
                          >
                            {element.text || "Yazmak için çift tıklayın"}
                          </div>
                        ) : element.type === "table" ? (
                          <>
                            <svg viewBox={`0 0 ${element.width} ${element.height}`} className="absolute inset-0 h-full w-full overflow-visible">
                              <rect
                                x="0"
                                y="0"
                                width={element.width}
                                height={element.height}
                                fill={element.fillColor || "#ffffff"}
                                stroke={element.strokeColor}
                                strokeWidth={element.strokeWidth}
                              />
                              {Array.from({ length: element.rows - 1 }, (_, rowIndex) => {
                                const y = (element.height / element.rows) * (rowIndex + 1);
                                return (
                                  <line
                                    key={`table_row_${rowIndex}`}
                                    x1="0"
                                    y1={y}
                                    x2={element.width}
                                    y2={y}
                                    stroke={element.strokeColor}
                                    strokeWidth={element.strokeWidth}
                                  />
                                );
                              })}
                              {Array.from({ length: element.columns - 1 }, (_, columnIndex) => {
                                const x = (element.width / element.columns) * (columnIndex + 1);
                                return (
                                  <line
                                    key={`table_col_${columnIndex}`}
                                    x1={x}
                                    y1="0"
                                    x2={x}
                                    y2={element.height}
                                    stroke={element.strokeColor}
                                    strokeWidth={element.strokeWidth}
                                  />
                                );
                              })}
                            </svg>
                            <div
                              className="absolute inset-0 z-[3] grid"
                              style={{
                                gridTemplateColumns: `repeat(${element.columns}, minmax(0, 1fr))`,
                                gridTemplateRows: `repeat(${element.rows}, minmax(0, 1fr))`,
                              }}
                            >
                              {Array.from({ length: element.rows }, (_, rowIndex) =>
                                Array.from({ length: element.columns }, (_, columnIndex) => {
                                  const contentBounds = getTableCellContentBounds(element, rowIndex, columnIndex);
                                  const cellText = getTableCellText(element, rowIndex, columnIndex);
                                  const domCellWidth = Math.max(8, contentBounds.width * sketchViewportScale.x);
                                  const domCellHeight = Math.max(8, contentBounds.height * sketchViewportScale.y);
                                  const textLayout = getFittedTableCellTextLayout(cellText, domCellWidth, domCellHeight);
                                  const isActiveCell =
                                    activeTableCellEdit?.elementId === element.id &&
                                    activeTableCellEdit.row === rowIndex &&
                                    activeTableCellEdit.column === columnIndex;
                                  const cellPaddingY = Math.min(TABLE_CELL_TEXT_PADDING_Y, contentBounds.height / 6);
                                  const cellPaddingX = Math.min(TABLE_CELL_TEXT_PADDING_X, contentBounds.width / 8);
                                  const displayedFontSize = Math.max(10, Math.round(textLayout.fontSize));
                                  const displayedLineHeight = Math.max(12, Math.round(textLayout.lineHeight));
                                  const displayedPaddingY = Math.max(2, Math.round(cellPaddingY * sketchViewportScale.y));
                                  const displayedPaddingX = Math.max(2, Math.round(cellPaddingX * sketchViewportScale.x));

                                  return (
                                    <div
                                      key={`${element.id}_${rowIndex}_${columnIndex}`}
                                      className="pointer-events-none min-h-0 min-w-0"
                                      style={{
                                        boxSizing: "border-box",
                                        overflow: "hidden",
                                        padding: `${Math.max(2, element.strokeWidth)}px`,
                                        width: "100%",
                                        height: "100%",
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                      }}
                                    >
                                      <div
                                        className={`flex h-full w-full items-center justify-center whitespace-pre-wrap break-words text-center ${
                                          isActiveCell ? "bg-indigo-100/70" : isSelected ? "bg-indigo-50/45" : ""
                                        }`}
                                        style={{
                                          color: element.strokeColor,
                                          fontSize: `${displayedFontSize}px`,
                                          lineHeight: `${displayedLineHeight}px`,
                                          boxSizing: "border-box",
                                          overflow: "hidden",
                                          width: "100%",
                                          height: "100%",
                                          maxWidth: "100%",
                                          maxHeight: "100%",
                                          minWidth: 0,
                                          minHeight: 0,
                                          padding: `${displayedPaddingY}px ${displayedPaddingX}px`,
                                          wordBreak: "break-word",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {textLayout.lines.length ? textLayout.lines.join("\n") : ""}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            {isSelected ? (
                              <div
                                onPointerDown={(event) => startSketchElementMove(event, element)}
                                className="absolute -top-2 -left-2 z-[5] rounded-full border border-indigo-400 bg-white px-2 py-0.5 text-[10px] font-medium text-indigo-700 shadow-sm cursor-move"
                              >
                                Taşı
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <svg viewBox={`0 0 ${element.width} ${element.height}`} className="h-full w-full overflow-visible">
                            {element.type === "triangle" ? (
                              <polygon
                                points={`${element.width / 2},0 ${element.width},${element.height} 0,${element.height}`}
                                fill={element.fillColor || "#ffffff"}
                                stroke={element.strokeColor}
                                strokeWidth={element.strokeWidth}
                              />
                            ) : element.type === "circle" ? (
                              <ellipse
                                cx={element.width / 2}
                                cy={element.height / 2}
                                rx={element.width / 2}
                                ry={element.height / 2}
                                fill={element.fillColor || "#ffffff"}
                                stroke={element.strokeColor}
                                strokeWidth={element.strokeWidth}
                              />
                            ) : (
                              <rect
                                x="0"
                                y="0"
                                width={element.width}
                                height={element.height}
                                fill={element.fillColor || "#ffffff"}
                                stroke={element.strokeColor}
                                strokeWidth={element.strokeWidth}
                              />
                            )}
                          </svg>
                        )}

                        {isSelected ? (
                          <div
                            onPointerDown={(event) => startSketchElementResize(event, element)}
                            onPointerMove={handleSketchOverlayPointerMove}
                            onPointerUp={handleSketchOverlayPointerUp}
                            className="absolute -bottom-1.5 -right-1.5 h-4 w-4 rounded-full border border-indigo-500 bg-white shadow-sm cursor-se-resize"
                          />
                        ) : null}
                      </div>
                    );
                  })}

                  {draftSketchElement ? (
                    <div
                      className="absolute border-2 border-dashed border-indigo-400 bg-indigo-100/30 pointer-events-none"
                      style={{
                        left: `${(draftSketchElement.x / SKETCH_CANVAS_WIDTH) * 100}%`,
                        top: `${(draftSketchElement.y / SKETCH_CANVAS_HEIGHT) * 100}%`,
                        width: `${(draftSketchElement.width / SKETCH_CANVAS_WIDTH) * 100}%`,
                        height: `${(draftSketchElement.height / SKETCH_CANVAS_HEIGHT) * 100}%`,
                      }}
                    />
                  ) : null}

                  {pendingDroppedItem ? (
                    <div className="pointer-events-none absolute right-3 top-3 rounded-full bg-indigo-600 px-3 py-1 text-[11px] font-medium text-white shadow">
                      {pendingDroppedItem === "text"
                        ? "Metin kutusunu buraya bırakın"
                        : pendingDroppedItem === "table"
                          ? "Tabloyu buraya bırakın"
                          : "Şekli buraya bırakın"}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="mt-2 text-[11px] text-gray-500">
                Metin, tablo ve şekiller için `Şekiller` butonunu açıp panelden canvas içine sürükleyip bırakın. Tablo bırakırken satır ve sütun sayısı sorulur. Eklenen nesneleri taşıyabilir ve sağ alt tutamaçtan yeniden boyutlandırabilirsiniz.
              </div>

              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  onClick={useSketchAsImageForActiveQuestion}
                  disabled={sketchGenerating || imageGenerating}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60 text-sm">
                  Eskizi Aynen Kullan
                </button>
                <button
                  onClick={generateImageFromSketchForActiveQuestion}
                  disabled={sketchGenerating || imageGenerating}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 text-sm">
                  {sketchGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  Çizimden Oluştur
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-indigo-200 bg-white p-4 space-y-4">
              <input
                ref={referenceImageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={handleReferenceImageUpload}
              />

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-gray-800">Referans görsel</div>
                  <div className="text-xs text-gray-500 mt-1">Fotoğrafı doğrudan kullanabilir veya prompt ile yeni bir varyasyon üretebilirsiniz.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => referenceImageInputRef.current?.click()}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm">
                    Görsel Yükle
                  </button>
                  {activeQuestion.referenceImage && (
                    <button
                      type="button"
                      onClick={() => updateActiveQuestionAndPersist((q) => ({ ...q, referenceImage: null }))}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm">
                      Referansı Kaldır
                    </button>
                  )}
                </div>
              </div>

              {activeQuestion.referenceImage ? (
                <div className="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)] gap-5 items-start">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
                    <img
                      src={`data:${activeQuestion.referenceImage.mimeType};base64,${activeQuestion.referenceImage.base64}`}
                      alt="Referans görsel"
                      className="w-full h-[220px] object-contain bg-white"
                    />
                    <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 bg-white">
                      {activeQuestion.referenceImage.fileName || "Yüklenen görsel"}
                    </div>
                  </div>

                  <div className="space-y-4 min-w-0">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">Referansı nasıl kullanmak istiyorsunuz?</label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={useReferenceImageDirectlyForActiveQuestion}
                          disabled={imageGenerating}
                          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60 text-sm">
                          Doğrudan Kullan
                        </button>
                        <button
                          onClick={generateImageFromReferenceForActiveQuestion}
                          disabled={imageGenerating}
                          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 text-sm">
                          {imageGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                          Referans Alarak Oluştur
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">AI için prompt</label>
                      <textarea
                        value={imagePrompt}
                        onChange={(e) => setImagePrompt(e.target.value)}
                        placeholder="Örnek: Bu fotoğrafı sadeleştir, eğitim kitabına uygun beyaz arka planlı temiz bir illüstrasyona dönüştür."
                        className="w-full min-h-[140px] border border-indigo-200 rounded-lg px-3 py-2 text-sm bg-white"
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        `Doğrudan Kullan` için prompt gerekmez. `Referans Alarak Oluştur` seçeneğinde bu açıklama AI'a gönderilir.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 px-4 py-10 text-center">
                  <div className="text-sm font-medium text-gray-700">Henüz görsel yüklenmedi</div>
                  <div className="mt-1 text-xs text-gray-500">PNG, JPG, WEBP veya GIF yükleyebilirsiniz.</div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    ) : null;

  if (loading) {
    return (
      <div className="h-full min-h-[calc(100vh-60px)] bg-gray-50 p-4 md:p-6">
        <div className="mx-auto h-full max-w-[1600px] animate-pulse">
          <div className="mb-4 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 md:flex-row md:items-center md:justify-between md:p-5">
            <div className="space-y-3">
              <div className="h-8 w-56 rounded-lg bg-gray-100" />
              <div className="h-4 w-40 rounded bg-gray-100" />
            </div>
            <div className="inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 text-sm text-gray-700 shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Dizgici yükleniyor...
            </div>
          </div>

          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[300px_1fr] xl:grid-cols-[300px_minmax(0,0.9fr)_minmax(320px,0.65fr)]">
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="mb-3 h-6 w-24 rounded bg-gray-100" />
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="rounded-lg border border-gray-100 p-3">
                    <div className="h-4 w-20 rounded bg-gray-100" />
                    <div className="mt-2 h-3 w-full rounded bg-gray-100" />
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-5">
              <div className="space-y-5">
                <div className="h-6 w-28 rounded bg-gray-100" />
                <div className="h-32 w-full rounded-xl bg-gray-100" />
                <div className="space-y-2">
                  <div className="h-5 w-24 rounded bg-gray-100" />
                  <div className="h-28 w-full rounded-xl bg-gray-100" />
                </div>
                <div className="space-y-3">
                  <div className="h-5 w-40 rounded bg-gray-100" />
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="grid grid-cols-[28px_1fr_130px] gap-2 items-center md:grid-cols-[36px_1fr_160px]">
                      <div className="h-4 w-4 rounded bg-gray-100" />
                      <div className="h-12 rounded-xl bg-gray-100" />
                      <div className="h-5 w-24 rounded bg-gray-100" />
                    </div>
                  ))}
                </div>
                <div className="h-24 w-full rounded-xl bg-gray-100" />
              </div>
            </div>

            <div className="hidden rounded-xl border border-gray-200 bg-white p-4 md:p-5 xl:block">
              <div className="space-y-4">
                <div className="h-6 w-28 rounded bg-gray-100" />
                <div className="rounded-xl border border-gray-100 p-4">
                  <div className="h-4 w-16 rounded bg-gray-100" />
                  <div className="mt-4 h-5 w-full rounded bg-gray-100" />
                  <div className="mt-4 space-y-3">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div key={index} className="h-12 rounded-xl bg-gray-100" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !state) {
    return (
      <div className="h-full min-h-[calc(100vh-60px)] p-4 md:p-6 bg-gray-50">
        <div className="max-w-3xl mx-auto bg-white border border-red-200 rounded-xl p-6">
          <div className="text-lg font-semibold text-gray-900 mb-2">Dizgici açılamadı</div>
          <div className="text-sm text-red-600 mb-4">{loadError || "Taslak verisi bulunamadı."}</div>
          <button
            onClick={() => void handleNavigateToList()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Listeye Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {renderedMathPicker}
      {renderedImageModal}
      <div className="h-full min-h-[calc(100vh-60px)] p-4 md:p-6 bg-gray-50">
        <div className="max-w-[1600px] mx-auto h-full">
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-5 mb-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={() => void handleNavigateToList()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs">
                <ArrowLeft className="w-3.5 h-3.5" />
                Liste
              </button>
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <Save className="w-3.5 h-3.5" />
                {isSaving ? "Kaydediliyor..." : lastSavedAt ? `Son kayıt: ${new Date(lastSavedAt).toLocaleTimeString("tr-TR")}` : "Kayıt bekleniyor"}
              </span>
            </div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Test Üretimi</h1>
            <p className="text-sm text-gray-600 mt-1">
              {state ? `${state.test_adi} • ${state.soru_sayisi} soru` : "Önce test bilgilerini girin"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLayoutSettingsOpen(true)}
              className="px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 text-sm">
              Soru Düzeni
            </button>
            <button
              onClick={() => setSetupOpen(true)}
              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">
              Test Bilgisi
            </button>
            <button
              onClick={exportZip}
              disabled={!state || exporting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-60 hover:bg-blue-700 text-sm font-medium">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              ZIP Olarak İndir
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-start lg:grid-cols-[300px_1fr] xl:grid-cols-[300px_1fr] gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col min-h-0 max-h-[calc(100vh-220px)]">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-800">Sorular</h2>
              <button
                onClick={addQuestion}
                disabled={!state}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-emerald-600 text-white disabled:opacity-60 hover:bg-emerald-700">
                <Plus className="w-3.5 h-3.5" /> Yeni
              </button>
            </div>

            <div className="overflow-auto pr-1 space-y-2">
              {state?.sorular.map((question) => (
                <div
                  key={question.id}
                  draggable
                  onDragStart={() => handleDragStart(question.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(question.id)}
                  className={`group border rounded-lg p-2.5 cursor-pointer transition ${
                    activeQuestionId === question.id ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                  }`}>
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <button onClick={() => setActiveQuestionId(question.id)} className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-1.5">
                        <div className="text-sm font-medium text-gray-800">Soru {question.sira}</div>
                        {question.referenceImage && (
                          <span
                            title="Referans görsel mevcut"
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 shrink-0">
                            Ref
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">
                        {question.soru_metni || "Henüz metin girilmedi"}
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <button
                        onClick={() => deleteQuestion(question.id)}
                        className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-5 overflow-visible">
            {!activeQuestion ? (
              <div className="h-full flex items-center justify-center text-gray-500">Düzenlemek için soldan bir soru seçin.</div>
            ) : (
              <div className="space-y-5">
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">Soru {activeQuestion.sira}</div>
                      <div className="text-xs text-gray-500 mt-1">{activeQuestionLayoutSummary || activeTemplateOption?.label}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPreviewModalOpen(true)}
                      className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Eye className="h-4 w-4" />
                      Önizleme
                    </button>
                  </div>

                  {activeQuestion.image && (
                    <div className="mb-3 space-y-3">
                      <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                        <QuestionImagePreview
                          image={activeQuestion.image}
                          alt="Soru görseli"
                          imageClassName="w-full max-h-[300px] object-contain bg-white"
                        />
                      </div>

                      <div className="rounded-lg border border-sky-200 bg-sky-50/70 px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-sky-950">Görsel Boyutu</div>
                            <div className="mt-1 text-xs text-sky-900/75">
                              Ön izlemede ve dışa aktarmada soru görselinin kapladığı alanı ayarlar.
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              updateActiveQuestion((q) => ({
                                ...q,
                                image: q.image
                                  ? {
                                      ...q.image,
                                      scale: 1,
                                    }
                                  : null,
                              }))
                            }
                            className="shrink-0 rounded-md border border-sky-200 bg-white px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-100"
                          >
                            Sıfırla
                          </button>
                        </div>
                        <div className="mt-3 flex items-center gap-3">
                          <input
                            type="range"
                            min={MIN_QUESTION_IMAGE_SCALE}
                            max={MAX_QUESTION_IMAGE_SCALE}
                            step={0.05}
                            value={activeQuestion.image.scale || 1}
                            onChange={(e) => {
                              const nextValue = clampQuestionImageScale(Number(e.target.value));
                              updateActiveQuestion((q) => ({
                                ...q,
                                image: q.image
                                  ? {
                                      ...q.image,
                                      scale: nextValue,
                                    }
                                  : null,
                              }));
                            }}
                            className="w-full accent-sky-600"
                          />
                          <div className="w-14 shrink-0 text-right text-sm font-semibold text-sky-900">
                            {`${(activeQuestion.image.scale || 1).toFixed(2)}x`}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-rose-950">Görsel Alt Kırpma</div>
                            <div className="mt-1 text-xs text-rose-900/75">
                              Görselin altındaki şık var ise bu alanı artırın. Dışa aktarmada sadece alt kısım kesilir.
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              updateActiveQuestion((q) => ({
                                ...q,
                                image: q.image
                                  ? {
                                      ...q.image,
                                      trimBottomRatio: 0,
                                    }
                                  : null,
                              }))
                            }
                            className="shrink-0 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
                          >
                            Sıfırla
                          </button>
                        </div>
                        <div className="mt-3 flex items-center gap-3">
                          <input
                            type="range"
                            min={0}
                            max={MAX_IMAGE_TRIM_BOTTOM_RATIO}
                            step={0.02}
                            value={activeQuestion.image.trimBottomRatio || 0}
                            onChange={(e) => {
                              const nextValue = clampImageTrimBottomRatio(Number(e.target.value));
                              updateActiveQuestion((q) => ({
                                ...q,
                                image: q.image
                                  ? {
                                      ...q.image,
                                      trimBottomRatio: nextValue,
                                    }
                                  : null,
                              }));
                            }}
                            className="w-full accent-rose-600"
                          />
                          <div className="w-14 shrink-0 text-right text-sm font-semibold text-rose-900">
                            %{Math.round((activeQuestion.image.trimBottomRatio || 0) * 100)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {activeQuestion.templateType === "image_with_caption" ? (
                  <div>
                    <label className="text-sm font-semibold text-gray-800 block mb-2">Üst Açıklama</label>
                    <div className="mb-3 text-xs text-gray-500">Bu alan sadece görselli sorularda kullanılır.</div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => updateActiveQuestion((q) => ({ ...q, showIntroText: false, intro_text: "" }))}
                        className={`rounded-lg border px-3 py-2 text-left text-sm transition ${
                          !activeQuestion.showIntroText
                            ? "border-blue-500 bg-blue-50 text-blue-900"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}>
                        <div className="font-medium">Üst yazı olmasın</div>
                        <div className="mt-1 text-xs text-gray-500">Görsel doğrudan soru bloğuna bağlansın.</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          updateActiveQuestion((q) => ({ ...q, showIntroText: true }));
                          window.requestAnimationFrame(() => introTextRef.current?.focus());
                        }}
                        className={`rounded-lg border px-3 py-2 text-left text-sm transition ${
                          activeQuestion.showIntroText
                            ? "border-blue-500 bg-blue-50 text-blue-900"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}>
                        <div className="font-medium">Üst yazı olsun</div>
                        <div className="mt-1 text-xs text-gray-500">Görselin üstünde kısa açıklama alanı kullan.</div>
                      </button>
                    </div>
                    {activeQuestion.showIntroText ? (
                      <>
                        <textarea
                          ref={introTextRef}
                          value={activeQuestion.intro_text || ""}
                          onChange={(e) => updateActiveQuestion((q) => ({ ...q, intro_text: e.target.value }))}
                          className="w-full min-h-[88px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          placeholder="Görselin üstünde görünecek kısa açıklamayı yazın"
                          spellCheck={false}
                        />
                        <div className="mt-2 text-xs text-gray-500">Bu alan soru metninden ayrıdır. Boş bırakırsanız export içinde boş satır oluşmaz.</div>
                      </>
                    ) : null}
                  </div>
                ) : null}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-800">Soru Metni</label>
                    <div className="flex items-center gap-2 relative">
                      <div className="relative">
                        <button
                          ref={(element) => {
                            mathPickerButtonRefs.current.question = element;
                          }}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => toggleMathPicker("question")}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border transition ${
                            openMathPicker?.questionId === activeQuestion.id && openMathPicker.field === "question"
                              ? "border-slate-800 bg-slate-900 text-white"
                              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          Semboller
                        </button>
                      </div>
                      <button
                        onClick={correctActiveQuestionText}
                        disabled={isCorrectingText || isTranscribing}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 disabled:opacity-60">
                        {isCorrectingText ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {isCorrectingText ? "Düzeltiliyor..." : "İmla Düzelt"}
                      </button>
                      <button
                        onClick={toggleRecording}
                        disabled={isTranscribing || isCorrectingText}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${
                          isRecording ? "bg-red-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}>
                        {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                        {isRecording ? "Kaydı Durdur" : isTranscribing ? "Dönüştürülüyor..." : "Dikte"}
                      </button>
                    </div>
                  </div>
                  <div className="mb-4 rounded-xl border border-cyan-200 bg-cyan-50/60 p-3 md:p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <label className="block text-sm font-semibold text-cyan-900">Görsel Ekle</label>
                        <div className="mt-1 text-sm text-cyan-900/70">Soru görselini buradan yönetin.</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setImageModalOpen(true)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700">
                        <ImagePlus className="w-4 h-4" />
                        Görsel Düzenleyici
                      </button>
                    </div>
                  </div>
                  <RichContentEditor
                    ref={questionEditorRef}
                    value={activeQuestion.questionContent}
                    onChange={(document) => updateActiveQuestion((q) => setQuestionContent(q, document))}
                    placeholder={activeQuestion.templateType === "image_with_caption" ? "Görselin altındaki soru metnini yazın" : "Soru metnini yazın"}
                  />
                  <div className="mt-2 text-xs text-gray-500">
                    Metin seçip <span className="font-semibold">Kalın</span>, <span className="font-semibold">İtalik</span> veya <span className="font-semibold">Altı Çizili</span> uygulayabilirsiniz. Bu vurgular dışa aktarmada korunur.
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <label className="text-sm font-semibold text-gray-800">Şıklar ve Doğru Cevap</label>
                      <div className="text-xs text-gray-500 mt-1">
                        {activeQuestion.choiceLayout === "horizontal"
                          ? "Önizleme ve export içinde yatay/kompakt yerleşim kullanılacak."
                          : activeQuestion.choiceLayout === "staggered"
                            ? "Önizleme ve export içinde çapraz/kademeli şık yerleşimi kullanılacak."
                            : "Şıklar klasik alt alta düzende yerleşecek."}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500">Şık sayısı: {activeQuestion.secenek_sayisi}</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateActiveQuestion((q) => {
                            const nextCount = clampChoiceCount(q.secenek_sayisi - 1);
                            if (nextCount === q.secenek_sayisi) {
                              return q;
                            }

                            const visibleKeys = getVisibleChoiceKeys(nextCount);
                            const nextSiklar = { ...q.siklar };
                            const nextChoiceContents = { ...(q.choiceContents || {}) };
                            CHOICE_KEYS.filter((key) => !visibleKeys.includes(key)).forEach((key) => {
                              nextSiklar[key] = "";
                              nextChoiceContents[key] = createRichContentDocument("");
                            });

                            return {
                              ...q,
                              siklar: nextSiklar,
                              choiceContents: nextChoiceContents,
                              secenek_sayisi: nextCount,
                              dogru_cevap: q.dogru_cevap && visibleKeys.includes(q.dogru_cevap) ? q.dogru_cevap : null,
                            };
                          })
                        }
                        disabled={activeQuestion.secenek_sayisi <= MIN_CHOICE_COUNT}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Şık Azalt
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          updateActiveQuestion((q) => ({
                            ...q,
                            secenek_sayisi: clampChoiceCount(q.secenek_sayisi + 1),
                          }))
                        }
                        disabled={activeQuestion.secenek_sayisi >= MAX_CHOICE_COUNT}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Şık Ekle
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {activeChoiceKeys.map((choice) => (
                      <div key={choice} className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                        <div className="mb-3 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-semibold text-gray-700 shadow-sm">
                              {choice}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-800">{choice} Şıkkı</div>
                              <div className="text-xs text-gray-500">Metin blokları arasına görsel ekleyebilir, sıralamayı değiştirebilirsiniz.</div>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              ref={(element) => {
                                mathPickerButtonRefs.current[choice] = element;
                              }}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => toggleMathPicker(choice)}
                              className={`inline-flex items-center rounded-md border px-2.5 py-1.5 text-xs font-medium transition ${
                                openMathPicker?.questionId === activeQuestion.id && openMathPicker.field === choice
                                  ? "border-slate-800 bg-slate-900 text-white"
                                  : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                              }`}
                            >
                              Semboller
                            </button>
                            <label className="inline-flex items-center gap-2 text-xs md:text-sm text-gray-700">
                              <input
                                type="radio"
                                name={`dogru_${activeQuestion.id}`}
                                checked={activeQuestion.dogru_cevap === choice}
                                onChange={() => updateActiveQuestion((q) => ({ ...q, dogru_cevap: choice }))}
                              />
                              Doğru Cevap
                            </label>
                          </div>
                        </div>
                        <RichContentEditor
                          ref={(element) => {
                            choiceEditorRefs.current[choice] = element;
                          }}
                          value={getChoiceContent(activeQuestion, choice)}
                          onChange={(document) => updateActiveQuestion((q) => setChoiceContent(q, choice, document))}
                          placeholder={`${choice} şıkkı`}
                          allowImages
                          onGenerateImage={() => openChoiceImageModal(choice)}
                          onUploadImage={() => triggerChoiceImageUpload(choice)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}
          </div>

          <input
            ref={richImageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleRichChoiceImageUpload}
          />
        </div>
      </div>

      <Modal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        title="Soru Önizleme"
        size="xl"
        panelClassName="sm:w-[min(1280px,calc(100vw-32px))] sm:max-w-[min(1280px,calc(100vw-32px))] h-screen sm:h-[88vh]"
        bodyClassName="h-[calc(100vh-64px)] sm:h-[calc(88vh-72px)] overflow-y-auto"
      >
        {!activeQuestion ? (
          <div className="h-40 flex items-center justify-center text-gray-500">Test önizleme için soru seçin.</div>
        ) : (
          <div className="mx-auto w-full max-w-4xl">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Test Önizleme</h3>
                <div className="mt-1 text-xs text-gray-500">
                  {previewQuestionIndex + 1}/{previewQuestionCount} • Kalan {remainingPreviewQuestionCount} soru
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goToPreviewQuestion("previous")}
                  disabled={previewQuestionIndex <= 0}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Geri
                </button>
                <div className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                  Soru {activeQuestion.sira}
                </div>
                <button
                  type="button"
                  onClick={() => goToPreviewQuestion("next")}
                  disabled={previewQuestionIndex >= previewQuestionCount - 1}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  İleri
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white p-4 md:p-6">
              <div className="mb-2 text-xs font-semibold tracking-wide text-slate-500">Soru {activeQuestion.sira}</div>
              {activeQuestion.templateType === "image_with_caption" && activeQuestion.showIntroText && activeQuestion.intro_text?.trim() ? (
                <div className={`mb-3 whitespace-pre-wrap rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 ${PREVIEW_RICH_TEXT_CLASS_NAME} text-amber-950`}>
                  {activeQuestion.intro_text}
                </div>
              ) : null}

              {activeQuestion.templateType === "image_with_caption" && !activeQuestion.image ? (
                <div className="mb-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 px-3 py-3 text-xs text-amber-900">
                  Bu şablonda görsel alanı üst açıklama ile soru metni arasına yerleşir. Görsel eklenirse gerçek dizgi görünümüne daha yakın olur.
                </div>
              ) : null}

              {activeQuestion.image && (
                <div className="mb-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
                  <QuestionImagePreview
                    image={activeQuestion.image}
                    alt="Önizleme görseli"
                    imageClassName="w-full max-h-[360px] object-contain"
                  />
                </div>
              )}

              <div className="mb-4">
                {activeQuestion.soru_metni ? (
                  <RichContentRenderer
                    value={activeQuestion.questionContent}
                    textClassName={PREVIEW_RICH_TEXT_CLASS_NAME}
                    imageClassName="max-h-64 w-auto rounded-lg border border-gray-200 bg-white object-contain"
                  />
                ) : (
                  <p className="text-sm leading-6 text-gray-500">Soru metni girildiğinde burada test görünümünde gösterilir.</p>
                )}
              </div>

              {renderQuestionPreviewChoices(activeQuestion, activeChoiceKeys)}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={choiceImageModalOpen}
        onClose={closeChoiceImageModal}
        title={choiceImageTarget ? `${choiceImageTarget} Şıkkı İçin Görsel Oluştur` : "Şık İçin Görsel Oluştur"}
        size="xl"
        closeOnBackdrop={!choiceImageGenerating && !choiceSketchGenerating}
        closeOnEscape={!choiceImageGenerating && !choiceSketchGenerating}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                choiceImageInputMode === "text"
                  ? "border-indigo-400 bg-white shadow-sm"
                  : "border-indigo-200 bg-white/70 hover:bg-white"
              }`}
            >
              <input
                type="radio"
                name="choice_image_input_mode_modal"
                checked={choiceImageInputMode === "text"}
                onChange={() => setChoiceImageInputMode("text")}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">Yazı ile oluştur</div>
                <div className="mt-1 text-xs text-gray-500">Şık için istediğiniz görseli tarif edin.</div>
              </div>
            </label>

            <label
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                choiceImageInputMode === "draw"
                  ? "border-indigo-400 bg-white shadow-sm"
                  : "border-indigo-200 bg-white/70 hover:bg-white"
              }`}
            >
              <input
                type="radio"
                name="choice_image_input_mode_modal"
                checked={choiceImageInputMode === "draw"}
                onChange={() => setChoiceImageInputMode("draw")}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">Çizim ile oluştur</div>
                <div className="mt-1 text-xs text-gray-500">Canvas üzerinde çizip doğrudan veya AI ile görsele çevirin.</div>
              </div>
            </label>

            <label
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                choiceImageInputMode === "upload"
                  ? "border-indigo-400 bg-white shadow-sm"
                  : "border-indigo-200 bg-white/70 hover:bg-white"
              }`}
            >
              <input
                type="radio"
                name="choice_image_input_mode_modal"
                checked={choiceImageInputMode === "upload"}
                onChange={() => setChoiceImageInputMode("upload")}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">Fotoğraf yükle</div>
                <div className="mt-1 text-xs text-gray-500">Yükleyip doğrudan kullanın veya referansla üretin.</div>
              </div>
            </label>
          </div>

          {choiceImageInputMode === "text" ? (
            <div className="rounded-lg border border-indigo-200 bg-white p-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Resimde ne olsun?</label>
              <div className="flex flex-col gap-2 md:flex-row">
                <input
                  value={choiceImagePrompt}
                  onChange={(e) => setChoiceImagePrompt(e.target.value)}
                  placeholder="Örnek: tanzimat dönemi kitabı ve sade arka plan"
                  className="flex-1 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm"
                />
                <button
                  onClick={submitChoiceImageTextGeneration}
                  disabled={choiceImageGenerating}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {choiceImageGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  Görsel Oluştur
                </button>
              </div>
            </div>
          ) : choiceImageInputMode === "draw" ? (
            <div className="rounded-lg border border-indigo-200 bg-white p-4">
              {choiceImageTarget && activeQuestion
                ? containsMathNotation(extractPlainTextFromRichContent(getChoiceContent(activeQuestion, choiceImageTarget)))
                  ? (
                      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        Matematik notasyonu içeren çizimlerde AI sembolleri değiştirebilir. En güvenli sonuç için
                        önce `Eskizi Aynen Kullan` seçeneğini deneyin.
                      </div>
                    )
                  : null
                : null}
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-2 text-xs text-gray-700">
                  Renk
                  <input
                    type="color"
                    value={choiceSketchColor}
                    disabled={choiceSketchIsEraser}
                    onChange={(e) => setChoiceSketchColor(e.target.value)}
                    className="h-8 w-8 rounded border border-gray-300 p-0"
                  />
                </label>
                <label className="inline-flex items-center gap-2 text-xs text-gray-700">
                  Kalınlık
                  <input
                    type="range"
                    min={1}
                    max={16}
                    value={choiceSketchLineWidth}
                    onChange={(e) => setChoiceSketchLineWidth(Number(e.target.value))}
                  />
                  <span>{choiceSketchLineWidth}px</span>
                </label>
                <button
                  type="button"
                  onClick={() => setChoiceSketchIsEraser((prev) => !prev)}
                  className={`rounded border px-2 py-1 text-xs ${
                    choiceSketchIsEraser ? "border-gray-800 bg-gray-800 text-white" : "border-gray-300 bg-white text-gray-700"
                  }`}
                >
                  {choiceSketchIsEraser ? "Kalem" : "Silgi"}
                </button>
                <button
                  type="button"
                  onClick={undoChoiceSketch}
                  disabled={choiceSketchUndoSize === 0 || choiceSketchGenerating || choiceImageGenerating}
                  className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                >
                  <Undo2 className="h-3.5 w-3.5" />
                  Geri Al
                </button>
                <button
                  type="button"
                  onClick={clearChoiceSketch}
                  disabled={choiceSketchGenerating || choiceImageGenerating}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                >
                  Temizle
                </button>
              </div>

              <div className="relative h-[260px] w-full overflow-hidden rounded border border-dashed border-indigo-200 bg-white">
                <canvas
                  ref={choiceSketchCanvasRef}
                  width={SKETCH_CANVAS_WIDTH}
                  height={SKETCH_CANVAS_HEIGHT}
                  onPointerDown={handleChoiceSketchPointerDown}
                  onPointerMove={handleChoiceSketchPointerMove}
                  onPointerUp={handleChoiceSketchPointerUp}
                  onPointerLeave={handleChoiceSketchPointerUp}
                  className="absolute inset-0 h-full w-full touch-none"
                />
              </div>

              <div className="mt-3">
                <label className="mb-2 block text-sm font-medium text-gray-700">Çizim için prompt (opsiyonel ama önerilir)</label>
                <textarea
                  value={choiceImagePrompt}
                  onChange={(e) => setChoiceImagePrompt(e.target.value)}
                  placeholder="Örnek: Hücrenin bölünmesini anlatan sade biyoloji çizimi, eğitim kitabı stili"
                  className="min-h-[100px] w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm"
                />
              </div>

              {choiceSketchGenerating ? (
                <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Çizimden görsel oluşturuluyor, lütfen bekleyin...
                </div>
              ) : null}

              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  onClick={useChoiceSketchAsImage}
                  disabled={choiceSketchGenerating || choiceImageGenerating}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Eskizi Aynen Kullan
                </button>
                <button
                  onClick={generateChoiceImageFromSketch}
                  disabled={choiceSketchGenerating || choiceImageGenerating}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {choiceSketchGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  Çizimden Oluştur
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 rounded-lg border border-indigo-200 bg-white p-4">
              <input
                ref={choiceReferenceImageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={handleChoiceReferenceImageUpload}
              />

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-gray-800">Referans görsel</div>
                  <div className="mt-1 text-xs text-gray-500">Yükleyip doğrudan ekleyebilir veya şık için yeni varyasyon üretebilirsiniz.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => choiceReferenceImageInputRef.current?.click()}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-100"
                  >
                    Görsel Yükle
                  </button>
                  {choiceReferenceImage ? (
                    <button
                      type="button"
                      onClick={() => setChoiceReferenceImage(null)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Referansı Kaldır
                    </button>
                  ) : null}
                </div>
              </div>

              {choiceReferenceImage ? (
                <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[240px_minmax(0,1fr)]">
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                    <img
                      src={`data:${choiceReferenceImage.mimeType};base64,${choiceReferenceImage.base64}`}
                      alt="Şık referans görseli"
                      className="h-[220px] w-full bg-white object-contain"
                    />
                    <div className="border-t border-gray-200 bg-white px-3 py-2 text-xs text-gray-500">
                      {choiceReferenceImage.fileName || "Yüklenen görsel"}
                    </div>
                  </div>

                  <div className="min-w-0 space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">AI için prompt</label>
                      <textarea
                        value={choiceImagePrompt}
                        onChange={(e) => setChoiceImagePrompt(e.target.value)}
                        placeholder="Örnek: aynı kompozisyonu koruyarak eğitim kitabı stilinde sadeleştir"
                        className="min-h-[120px] w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={useChoiceReferenceImageDirectly}
                        disabled={choiceImageGenerating}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      >
                        Doğrudan Kullan
                      </button>
                      <button
                        onClick={generateChoiceImageFromReference}
                        disabled={choiceImageGenerating}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-60"
                      >
                        {choiceImageGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                        Referans Alarak Oluştur
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 px-4 py-10 text-center">
                  <div className="text-sm font-medium text-gray-700">Henüz görsel yüklenmedi</div>
                  <div className="mt-1 text-xs text-gray-500">PNG, JPG, WEBP veya GIF yükleyebilirsiniz.</div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

        <Modal isOpen={layoutSettingsOpen} onClose={() => setLayoutSettingsOpen(false)} title="Soru Düzeni" size="md">
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
              <div className="text-sm font-semibold text-amber-950">Soru Düzeni</div>
              <div className="text-xs text-amber-900/70 mt-1 mb-3">
                Bu seçimler sadece seçili soruya uygulanır.
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-900/80 mb-2">Üst Açıklama</div>
                  <div className="text-xs text-amber-900/70 mb-2">Sadece görselli sorular için uygulanır.</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        applyLayoutSettingsToActiveQuestion({
                          choiceLayout: resolvedChoiceLayout,
                          showIntroText: false,
                        })
                      }
                      className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                        resolvedShowIntroText === false
                          ? "border-blue-500 bg-white text-blue-900"
                          : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                      }`}>
                      <div className="font-semibold">Yok</div>
                      <div className="mt-1 text-xs text-gray-500">Görselli sorularda üst açıklama alanı kapalı olsun.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        applyLayoutSettingsToActiveQuestion({
                          choiceLayout: resolvedChoiceLayout,
                          showIntroText: true,
                        })
                      }
                      className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                        resolvedShowIntroText === true
                          ? "border-blue-500 bg-white text-blue-900"
                          : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                      }`}>
                      <div className="font-semibold">Olsun</div>
                      <div className="mt-1 text-xs text-gray-500">Görselli sorularda üst açıklama alanı açık olsun.</div>
                    </button>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-900/80 mb-2">Şık Yerleşimi</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        applyLayoutSettingsToActiveQuestion({
                          choiceLayout: "vertical",
                          showIntroText: resolvedShowIntroText,
                        })
                      }
                      className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                        resolvedChoiceLayout === "vertical"
                          ? "border-blue-500 bg-white text-blue-900"
                          : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                      }`}>
                      <div className="font-semibold">Alt Alta</div>
                      <div className="mt-1 text-xs text-gray-500">Klasik şık dizilimi.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        applyLayoutSettingsToActiveQuestion({
                          choiceLayout: "horizontal",
                          showIntroText: resolvedShowIntroText,
                        })
                      }
                      className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                        resolvedChoiceLayout === "horizontal"
                          ? "border-blue-500 bg-white text-blue-900"
                          : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                      }`}>
                      <div className="font-semibold">Yatay</div>
                      <div className="mt-1 text-xs text-gray-500">Şıklar yan yana dizilsin.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        applyLayoutSettingsToActiveQuestion({
                          choiceLayout: "staggered",
                          showIntroText: resolvedShowIntroText,
                        })
                      }
                      className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                        resolvedChoiceLayout === "staggered"
                          ? "border-blue-500 bg-white text-blue-900"
                          : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                      }`}>
                      <div className="font-semibold">Çapraz</div>
                      <div className="mt-1 text-xs text-gray-500">Şıklar kademeli yerleşsin.</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>

        <Modal isOpen={setupOpen} onClose={() => setSetupOpen(false)} title="Test Bilgileri" size="sm">
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Test Adı</label>
            <input
              value={setupData.test_adi}
              onChange={(e) => setSetupData((prev) => ({ ...prev, test_adi: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Örn: Edebiyat Test 1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Kategori (Opsiyonel)</label>
            <input
              value={setupData.kategori}
              onChange={(e) => setSetupData((prev) => ({ ...prev, kategori: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Örn: Edebiyat"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Soru Sayısı (1-200)</label>
            <input
              type="number"
              min={1}
              max={200}
              value={setupData.soru_sayisi}
              onChange={(e) =>
                setSetupData((prev) => ({
                  ...prev,
                  soru_sayisi: Number(e.target.value),
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="flex justify-end">
            <button onClick={updateSetupData} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm">
              Kaydet
            </button>
          </div>
        </div>
        </Modal>
      </div>
    </>
  );
}
